import type { GroundworksData } from "../types/tender.types";
import { CONCRETE_STRIP_CODE_MAP } from "./foundationsReferenceMapping";
import { EVALUATE_CODES, type EvaluateCode } from "./constants";

export interface GroundworksTakeoffImportResult {
  groundworks: GroundworksData;
  importedSheets: number;
  hasWallBuildUpData: boolean;
  pageChanges: Array<{ page: string; count: number }>;
}

/**
 * Parses a take-off Excel workbook for Groundworks sheets and merges the results
 * into the current GroundworksData. Extracted verbatim from JobTypeSelection.tsx's
 * performBulkImport (groundworks branch) so the legacy bulk-import modal and the
 * /tender/import-takeoff page share one implementation.
 */
export function importGroundworksTakeoff(
  file: File,
  currentGroundworks: GroundworksData,
): Promise<GroundworksTakeoffImportResult> {
  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const XLSX = await import("xlsx");
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: "array" });

          let importedSheets = 0;

          let cutFillsData: any[] = [];
          let cutFillsPrefixValues: Record<string, string> = {};
          type SoilSummaryData = {
            foundationsCutArisings: number; foundationsFill: number;
            slabPathsCut: number; slabPathsFill: number;
            gardensCut: number; gardensFill: number;
            drivesCut: number; drivesFill: number;
            foundationsArisingsCut: number; foundationsArisingsFill: number;
            plotDrainageCut: number; plotDrainageFill: number;
            wallsCut: number; wallsFill: number;
            plotServicesCut: number; plotServicesFill: number;
          };
          let soilManagementData: SoilSummaryData | null = null;
          let foundationsData: any[] = [];
          let foundationsPrefixValues: Record<string, string> = {};
          let drainageData: any[] = [];
          let drainageColumnHeaders: Record<string, string> = {};
          let drainageDimensionValues: Record<string, string> = {};
          let plotDrivesData: any[] = [];
          let plotDrivesColumnHeaders: Record<string, string> = {};
          let plotDrivesDimensionValues: Record<string, string> = {};
          let plotServiceData: any[] = [];
          let plotPavingsData: any[] = [];
          let plotPavingsColumnHeaders: Record<string, string> = {};
          let plotPavingsDimensionValues: Record<string, string> = {};
          let scaffoldData: any[] = [];
          let scaffoldColumnHeaders: Record<string, string> = {};
          let scaffoldDimensionValues: Record<string, string> = {};
          let gardenData: any[] = [];
          let gardenColumnHeaders: Record<string, string> = {};
          let gardenDimensionValues: Record<string, string> = {};
          let retScreenWallsData: any[] = [];
          let geoToberWallData: any[] = [];
          let geoToberWallColumnHeaders: Record<string, string> = {};
          let geoToberWallDimensionValues: Record<string, string> = {};
          let geoToberWallBuildUpDimensions: Record<string, Array<{ description: string; length: number; width: number; height: number; net: number; unit: string }>> = {};
          let wallsBuildUpDimensions: Record<string, Array<{ description: string; length: number; width: number; height: number; net: number; unit: string }>> = {};

          // Import Cut & Fills data from "PLOT C&F QUANTS" worksheet
          if (workbook.SheetNames.includes("PLOT C&F QUANTS")) {
            const worksheet = workbook.Sheets["PLOT C&F QUANTS"];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: true,
              blankrows: false,
            }) as any[][];
            // Process Cut & Fills data
            const cutFillsResult = processCutFillsData(jsonData);
            cutFillsData = cutFillsResult.data;
            cutFillsPrefixValues = cutFillsResult.prefixValues;
            if (cutFillsData && cutFillsData.length > 0) {
              importedSheets++;
            } else {
            }
          } else {
          }

          // Import Foundations data from "PLOT FOUNDS QUANTS" worksheet
          if (workbook.SheetNames.includes("PLOT FOUNDS QUANTS")) {
            const worksheet = workbook.Sheets["PLOT FOUNDS QUANTS"];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: true,
              blankrows: false,
            }) as any[][];
            // Process Foundations data
            const foundationsResult = processFoundationsData(jsonData);
            foundationsData = foundationsResult.data;
            foundationsPrefixValues = foundationsResult.prefixValues;
            if (foundationsData && foundationsData.length > 0) {
              importedSheets++;
            } else {
            }
          } else {
          }

          // Import Garden data from "GARDEN QUANTS" worksheet
          if (workbook.SheetNames.includes("GARDEN QUANTS")) {
            const worksheet = workbook.Sheets["GARDEN QUANTS"];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: true,
              blankrows: false,
            }) as any[][];
            const gardenResult = processGardenData(jsonData);
            gardenData = gardenResult.data;
            gardenColumnHeaders = gardenResult.columnHeaders;
            gardenDimensionValues = gardenResult.dimensionValues;
            if (gardenData && gardenData.length > 0) {
              importedSheets++;
            } else {
            }
          } else {
          }

          // Import Geowall & Tobermore Walls data from "GEO & TOBER WALL QUANTS " worksheet (note: trailing space in Excel)
          if (workbook.SheetNames.includes("GEO & TOBER WALL QUANTS ")) {
            const worksheet = workbook.Sheets["GEO & TOBER WALL QUANTS "];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: true,
              blankrows: false,
            }) as any[][];
            // Try multiple approaches to find header
            let headerRowIndex = -1;

            // Approach 1: Look for "Plot" and "No" in same cell
            for (let i = 0; i < Math.min(5, jsonData.length); i++) {
              const row = jsonData[i];
              if (
                row &&
                row.some(
                  (cell) =>
                    String(cell).includes("Plot") &&
                    String(cell).includes("No"),
                )
              ) {
                headerRowIndex = i;
                break;
              }
            }

            // Approach 2: Look for "plot" (case insensitive) in first cell
            if (headerRowIndex === -1) {
              for (let i = 0; i < Math.min(5, jsonData.length); i++) {
                const row = jsonData[i];
                if (row && row.length > 0) {
                  const firstCell = String(row[0] || "").toLowerCase();
                  if (firstCell.includes("plot")) {
                    headerRowIndex = i;
                    break;
                  }
                }
              }
            }

            // Approach 3: Use row with most columns (likely the header)
            if (headerRowIndex === -1) {
              let maxCols = 0;
              for (let i = 0; i < Math.min(5, jsonData.length); i++) {
                if (jsonData[i] && jsonData[i].length > maxCols) {
                  maxCols = jsonData[i].length;
                  headerRowIndex = i;
                }
              }
            }
            if (headerRowIndex === -1 || !jsonData[headerRowIndex]) {
              console.error("Could not find valid header row");
              geoToberWallData = [];
            } else {
              const headers = jsonData[headerRowIndex];
              const allDataRows = jsonData.slice(headerRowIndex + 1);
              if (allDataRows.length > 0) {
              }

              // Find where actual data starts - use all rows by default
              let dataStartIndex = 0;
              let foundDataStart = false;

              // Look for first row with valid plot number and house type
              for (let i = 0; i < Math.min(10, allDataRows.length); i++) {
                const row = allDataRows[i];
                if (row && row.length > 0) {
                  const plotNoValue = String(row[0] || "").trim();
                  const houseTypeValue = String(row[1] || "").trim();
                  // Look for first row that has both plot number and house type that look like actual data
                  const hasValidPlotNo =
                    plotNoValue &&
                    plotNoValue !== "" &&
                    !plotNoValue.toLowerCase().includes("total");
                  const hasValidHouseType =
                    houseTypeValue &&
                    houseTypeValue !== "" &&
                    !houseTypeValue.toLowerCase().includes("total") &&
                    !houseTypeValue.toLowerCase().includes("sum") &&
                    !houseTypeValue.toLowerCase().includes("note") &&
                    isNaN(Number(houseTypeValue)) && // Not purely numeric
                    houseTypeValue.match(/[a-zA-Z]/); // Contains letters

                  if (hasValidPlotNo && hasValidHouseType) {
                    dataStartIndex = i;
                    foundDataStart = true;
                    break;
                  }
                }
              }

              if (!foundDataStart) {
                dataStartIndex = 0;
              }

              const dataRows = allDataRows.slice(dataStartIndex);
              if (dataRows.length > 0) {
              }

              // Enhanced column detection function
              const findColumnIndex = (searchTerms: string[]) => {
                return headers.findIndex((header: any) => {
                  const headerStr = String(header || "")
                    .toLowerCase()
                    .trim();
                  return searchTerms.some((term) =>
                    headerStr.includes(term.toLowerCase()),
                  );
                });
              };
              // Basic columns - hardcoded because headers are null for first columns in this Excel file
              const plotNoIdx = 0; // Plot No is always column 0
              const housetypeIdx = 1; // House Type is always column 1
              // GEOWALLS section - comprehensive search terms
              const excavateTrenchAndTipOnSiteIdx = findColumnIndex([
                "excavate trench and tip on-site",
                "excavate trench and tip on site",
                "excavate trench",
                "tip on-site",
              ]);

              const compactFormationIdx = findColumnIndex([
                "compact formation",
              ]);

              const concreteBlindingIdx = findColumnIndex([
                "concrete blinding",
              ]);

              const a393MeshIdx = findColumnIndex(["a393 mesh"]);

              const a252MeshIdx = findColumnIndex(["a252 mesh"]);

              const b503MeshIdx = findColumnIndex(["b503 mesh"]);

              const b785MeshIdx = findColumnIndex(["b785 mesh"]);

              const concreteToFoundationIdx = findColumnIndex([
                "concrete to foundation",
              ]);

              const asDugBackfillIdx = findColumnIndex(["as dug backfill"]);

              const stockpileOnsiteIdx = findColumnIndex([
                "stockpile on-site",
                "stockpile onsite",
              ]);

              const perforated150mmPipeIdx = findColumnIndex([
                "150mmø perforated pipe",
                "150mm perforated pipe",
                "perforated 150mm pipe",
                "150mm pipe",
                "perforated pipe",
              ]);

              const concreteHaunchingToFrontIdx = findColumnIndex([
                "concrete haunching to front",
                "haunching to front",
              ]);

              const concreteHaunchingToRearIdx = findColumnIndex([
                "concrete haunching to rear",
                "haunching to rear",
              ]);

              const granularBackfillIdx = findColumnIndex([
                "granular backfill",
              ]);

              const granularBackfill2Idx = findColumnIndex([
                "granular backfill",
              ]);

              const terramIdx = findColumnIndex(["terram"]);

              const fencePostAt2400mmCcsIdx = findColumnIndex([
                "fence post at 2400mm ccs",
                "fence post 2400mm",
                "fence post",
              ]);

              const purchaseInclCarriageWasteFpIdx = findColumnIndex([
                "purchase incl carriage waste fp",
                "purchase incl carriage",
                "carriage waste",
              ]);

              const installGeoWallsIdx = findColumnIndex(["install geo walls"]);

              // TOBERMORE WALLS section - comprehensive search terms
              const excavateInCuttingsFpIdx = findColumnIndex([
                "excavate in cuttings fp",
                "excavate in cuttings",
                "cuttings fp",
              ]);

              const installGeoWallsTobermoreIdx = findColumnIndex([
                "install geo walls",
              ]);

              const excavationOfEmbankmentIdx = findColumnIndex([
                "excavation of embankment",
                "embankment",
              ]);

              // Stockpile Onsite m2 -> Column Y (index 24)
              const stockpileOnsiteTobermoreIdx = 24; // Column Y

              const batterToFaceOfEmbankmentIdx = findColumnIndex([
                "batter to face of embankment",
                "batter to face",
              ]);

              const excavationOfBaseIdx = findColumnIndex([
                "excavation of base",
              ]);

              // Stockpile Onsite m3 -> Column AB (index 27)
              const stockpileOnsite2Idx = 27; // Column AB

              // Level & compact formation m2 -> Column AC (index 28)
              const levelAndCompactFormationIdx = 28; // Column AC

              const concreteToBaseIdx = findColumnIndex(["concrete to base"]);

              const tobermoreSupplyOfGrandSecuraInclFpIdx = findColumnIndex([
                "tobermore supply of grand secura incl fp",
                "tobermore supply",
                "grand secura",
              ]);

              const tobermoreSecuraInstallationByMjIdx = findColumnIndex([
                "tobermore secura installation by mj",
                "secura installation",
                "installation by mj",
              ]);

              const tobermoreSupplyOfGeogridIdx = findColumnIndex([
                "tobermore supply of geogrid",
                "supply of geogrid",
              ]);

              const mjInstallationOfGeogridIdx = findColumnIndex([
                "mj installation of geogrid",
                "installation of geogrid",
              ]);

              // Fence post at 2400mm ccs m3 -> Column AI (index 34)
              const fencePostAt2400mmCcsTobermoreIdx = 34; // Column AI

              const freeDrainingGranularBackfill300wColumnIdx = findColumnIndex(
                [
                  "free draining granular backfill 300w column",
                  "free draining granular",
                  "300w column",
                ],
              );

              const granularBackfillToRearOfWallIdx = findColumnIndex([
                "granular backfill to rear of wall",
                "backfill to rear",
              ]);

              const landDrainToRearOfWallIdx = findColumnIndex([
                "land drain to rear of wall",
                "land drain",
              ]);

              const allowSiltTrapIdx = findColumnIndex([
                "allow silt trap",
                "silt trap",
              ]);

              const allowToConnectOfLandDrainToPlotDrainageIdx =
                findColumnIndex([
                  "allow to connect of land drain to plot drainage",
                  "connect land drain",
                  "land drain to plot drainage",
                ]);

              // Validation for essential columns (always passes now since hardcoded)
              if (true) {
                // Always true since we hardcoded the indices
                // Helper function to safely extract column values
                const getColumnValue = (
                  row: any[],
                  colIdx: number,
                  defaultValue: any = 0,
                ) => {
                  return colIdx !== -1 &&
                    row[colIdx] !== undefined &&
                    row[colIdx] !== null
                    ? row[colIdx]
                    : defaultValue;
                };
                const nonEmptyRows = dataRows.filter(
                  (row) =>
                    row &&
                    row.length > 0 &&
                    row.some(
                      (cell) =>
                        cell !== null && cell !== undefined && cell !== "",
                    ),
                );
                const allImportedRows = nonEmptyRows.map((row, index) => {
                  if (index < 3) {
                  }
                  return {
                    key: `imported_${Date.now()}_${index}`,
                    plotNo: String(getColumnValue(row, plotNoIdx, "")),
                    housetype: String(getColumnValue(row, housetypeIdx, "")),
                    // GEOWALLS
                    excavateTrenchAndTipOnSite: Number(
                      getColumnValue(row, excavateTrenchAndTipOnSiteIdx, 0),
                    ),
                    compactFormation: Number(
                      getColumnValue(row, compactFormationIdx, 0),
                    ),
                    concreteBlinding: Number(
                      getColumnValue(row, concreteBlindingIdx, 0),
                    ),
                    a393Mesh: Number(getColumnValue(row, a393MeshIdx, 0)),
                    a252Mesh: Number(getColumnValue(row, a252MeshIdx, 0)),
                    b503Mesh: Number(getColumnValue(row, b503MeshIdx, 0)),
                    b785Mesh: Number(getColumnValue(row, b785MeshIdx, 0)),
                    concreteToFoundation: Number(
                      getColumnValue(row, concreteToFoundationIdx, 0),
                    ),
                    asDugBackfill: Number(
                      getColumnValue(row, asDugBackfillIdx, 0),
                    ),
                    stockpileOnsite: Number(
                      getColumnValue(row, stockpileOnsiteIdx, 0),
                    ),
                    perforated150mmPipe: Number(
                      getColumnValue(row, perforated150mmPipeIdx, 0),
                    ),
                    concreteHaunchingToFront: Number(
                      getColumnValue(row, concreteHaunchingToFrontIdx, 0),
                    ),
                    concreteHaunchingToRear: Number(
                      getColumnValue(row, concreteHaunchingToRearIdx, 0),
                    ),
                    granularBackfill: Number(
                      getColumnValue(row, granularBackfillIdx, 0),
                    ),
                    granularBackfill2: Number(
                      getColumnValue(row, granularBackfill2Idx, 0),
                    ),
                    terram: Number(getColumnValue(row, terramIdx, 0)),
                    fencePostAt2400mmCcs: Number(
                      getColumnValue(row, fencePostAt2400mmCcsIdx, 0),
                    ),
                    purchaseInclCarriageWasteFp: Number(
                      getColumnValue(row, purchaseInclCarriageWasteFpIdx, 0),
                    ),
                    installGeoWalls: Number(
                      getColumnValue(row, installGeoWallsIdx, 0),
                    ),
                    // TOBERMORE WALLS
                    excavateInCuttingsFp: Number(
                      getColumnValue(row, excavateInCuttingsFpIdx, 0),
                    ),
                    installGeoWallsTobermore: Number(
                      getColumnValue(row, installGeoWallsTobermoreIdx, 0),
                    ),
                    excavationOfEmbankment: Number(
                      getColumnValue(row, excavationOfEmbankmentIdx, 0),
                    ),
                    stockpileOnsiteTobermore: Number(
                      getColumnValue(row, stockpileOnsiteTobermoreIdx, 0),
                    ),
                    batterToFaceOfEmbankment: Number(
                      getColumnValue(row, batterToFaceOfEmbankmentIdx, 0),
                    ),
                    excavationOfBase: Number(
                      getColumnValue(row, excavationOfBaseIdx, 0),
                    ),
                    stockpileOnsite2: Number(
                      getColumnValue(row, stockpileOnsite2Idx, 0),
                    ),
                    levelAndCompactFormation: Number(
                      getColumnValue(row, levelAndCompactFormationIdx, 0),
                    ),
                    concreteToBase: Number(
                      getColumnValue(row, concreteToBaseIdx, 0),
                    ),
                    tobermoreSupplyOfGrandSecuraInclFp: Number(
                      getColumnValue(
                        row,
                        tobermoreSupplyOfGrandSecuraInclFpIdx,
                        0,
                      ),
                    ),
                    tobermoreSecuraInstallationByMj: Number(
                      getColumnValue(
                        row,
                        tobermoreSecuraInstallationByMjIdx,
                        0,
                      ),
                    ),
                    tobermoreSupplyOfGeogrid: Number(
                      getColumnValue(row, tobermoreSupplyOfGeogridIdx, 0),
                    ),
                    mjInstallationOfGeogrid: Number(
                      getColumnValue(row, mjInstallationOfGeogridIdx, 0),
                    ),
                    fencePostAt2400mmCcsTobermore: Number(
                      getColumnValue(row, fencePostAt2400mmCcsTobermoreIdx, 0),
                    ),
                    freeDrainingGranularBackfill300wColumn: Number(
                      getColumnValue(
                        row,
                        freeDrainingGranularBackfill300wColumnIdx,
                        0,
                      ),
                    ),
                    granularBackfillToRearOfWall: Number(
                      getColumnValue(row, granularBackfillToRearOfWallIdx, 0),
                    ),
                    landDrainToRearOfWall: Number(
                      getColumnValue(row, landDrainToRearOfWallIdx, 0),
                    ),
                    allowSiltTrap: Number(
                      getColumnValue(row, allowSiltTrapIdx, 0),
                    ),
                    allowToConnectOfLandDrainToPlotDrainage: Number(
                      getColumnValue(
                        row,
                        allowToConnectOfLandDrainToPlotDrainageIdx,
                        0,
                      ),
                    ),
                  };
                });
                if (allImportedRows.length > 0) {
                }

                const validatedRows = allImportedRows.filter((row, index) => {
                  const plotNoStr = String(row.plotNo || "").trim();
                  const hasValidPlotNo =
                    plotNoStr !== "" &&
                    plotNoStr !== "0" &&
                    plotNoStr !== "null" &&
                    plotNoStr !== "undefined";
                  const housetypeStr = String(row.housetype || "").trim();
                  const hasValidHouseType =
                    housetypeStr !== "" &&
                    !housetypeStr.toLowerCase().includes("total") &&
                    !housetypeStr.toLowerCase().includes("sum") &&
                    isNaN(Number(housetypeStr)) && // Not purely numeric
                    housetypeStr.match(/[a-zA-Z]/); // Contains letters
                  if (!hasValidPlotNo && index < 5) {
                  }
                  return hasValidPlotNo && hasValidHouseType;
                });
                if (validatedRows.length === 0) {
                  console.error(
                    "No valid data found to import after validation",
                  );
                  geoToberWallData = [];
                } else {
                  // Count found columns for summary
                  const foundColumns = [
                    plotNoIdx,
                    housetypeIdx,
                    excavateTrenchAndTipOnSiteIdx,
                    compactFormationIdx,
                    concreteBlindingIdx,
                    a393MeshIdx,
                    a252MeshIdx,
                    b503MeshIdx,
                    b785MeshIdx,
                    concreteToFoundationIdx,
                    asDugBackfillIdx,
                    stockpileOnsiteIdx,
                    perforated150mmPipeIdx,
                    concreteHaunchingToFrontIdx,
                    concreteHaunchingToRearIdx,
                    granularBackfillIdx,
                    granularBackfill2Idx,
                    terramIdx,
                    fencePostAt2400mmCcsIdx,
                    purchaseInclCarriageWasteFpIdx,
                    installGeoWallsIdx,
                    excavateInCuttingsFpIdx,
                    installGeoWallsTobermoreIdx,
                    excavationOfEmbankmentIdx,
                    stockpileOnsiteTobermoreIdx,
                    batterToFaceOfEmbankmentIdx,
                    excavationOfBaseIdx,
                    stockpileOnsite2Idx,
                    levelAndCompactFormationIdx,
                    concreteToBaseIdx,
                    tobermoreSupplyOfGrandSecuraInclFpIdx,
                    tobermoreSecuraInstallationByMjIdx,
                    tobermoreSupplyOfGeogridIdx,
                    mjInstallationOfGeogridIdx,
                    fencePostAt2400mmCcsTobermoreIdx,
                    freeDrainingGranularBackfill300wColumnIdx,
                    granularBackfillToRearOfWallIdx,
                    landDrainToRearOfWallIdx,
                    allowSiltTrapIdx,
                    allowToConnectOfLandDrainToPlotDrainageIdx,
                  ].filter((idx) => idx !== -1).length;
                  geoToberWallData = validatedRows;
                }
              }
            }

            if (geoToberWallData && geoToberWallData.length > 0) {
              importedSheets++;
            } else {
              console.error(
                "❌ No Geowall & Tobermore Walls data to import - inline processing returned empty array",
              );
              console.error("Check the processing logs above for the reason");
            }
          } else {
            console.error(
              "❌ Worksheet 'GEO & TOBER WALL QUANTS ' (with trailing space) not found",
            );
            console.error("Available sheets:", workbook.SheetNames);
            console.error(
              "Note: The worksheet name must match exactly including any trailing spaces",
            );
          }

          // Import Plot Pavings data from "PLOT PAVING QUANTS" (new name) or
          // "PLOT PATHS QUANTS" (legacy name). Only one will be present.
          const plotPavingsSheetName = ["PLOT PAVING QUANTS", "PLOT PATHS QUANTS"].find(
            (name) => workbook.SheetNames.includes(name),
          );
          if (plotPavingsSheetName) {
            const worksheet = workbook.Sheets[plotPavingsSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: true,
              blankrows: false,
            }) as any[][];
            const plotPavingsResult = processPlotPavingsData(jsonData);
            plotPavingsData = plotPavingsResult.data;
            plotPavingsColumnHeaders = plotPavingsResult.columnHeaders;
            plotPavingsDimensionValues = plotPavingsResult.dimensionValues;
            if (plotPavingsData && plotPavingsData.length > 0) {
              importedSheets++;
            }
          }

          // Import Scaffold data from "SCAFFOLD QUANTS" worksheet
          if (workbook.SheetNames.includes("SCAFFOLD QUANTS")) {
            const worksheet = workbook.Sheets["SCAFFOLD QUANTS"];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: true,
              blankrows: false,
            }) as any[][];
            const scaffoldResult = processScaffoldData(jsonData);
            scaffoldData = scaffoldResult.data;
            scaffoldColumnHeaders = scaffoldResult.columnHeaders;
            scaffoldDimensionValues = scaffoldResult.dimensionValues;
            if (scaffoldData && scaffoldData.length > 0) {
              importedSheets++;
            } else {
            }
          } else {
          }

          // Import Plot Service data from "PLOT SERVICE QUANTS" worksheet
          const plotServiceSheetName = workbook.SheetNames.find((n) =>
            n.toUpperCase().includes("PLOT SERVICE"),
          );
          if (plotServiceSheetName) {
            const worksheet = workbook.Sheets[plotServiceSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: true,
              blankrows: false,
            }) as any[][];
            plotServiceData = processPlotServiceData(jsonData);
            if (plotServiceData && plotServiceData.length > 0) {
              importedSheets++;
            } else {
            }
          } else {
          }

          // Import Retaining Walls data from "RET & SCREEN TAKE OFF" worksheet
          if (workbook.SheetNames.includes("RET & SCREEN TAKE OFF")) {
            const worksheet = workbook.Sheets["RET & SCREEN TAKE OFF"];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: true,
              blankrows: false,
            }) as any[][];
            retScreenWallsData = processRetainingWallsData(jsonData);
            if (retScreenWallsData && retScreenWallsData.length > 0) {
              importedSheets++;
            }
            // Also extract Geowall & Tobermore data from same sheet
            const geoToberResult = processGeoToberWallFromRetScreenData(jsonData);
            if (geoToberResult.data.length > 0) {
              geoToberWallData = geoToberResult.data;
              geoToberWallColumnHeaders = geoToberResult.columnHeaders;
              geoToberWallDimensionValues = geoToberResult.dimensionValues;
            }
          } else {
          }

          // Import Geowall build-up data from "GEOWALL MASTER" worksheet
          if (workbook.SheetNames.includes("GEOWALL MASTER")) {
            const worksheet = workbook.Sheets["GEOWALL MASTER"];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: true,
              blankrows: false,
            }) as any[][];
            const parsed = processGeoWallMasterData(jsonData);
            if (Object.keys(parsed).length > 0) {
              Object.assign(geoToberWallBuildUpDimensions, parsed);
              importedSheets++;
            }
          }

          // Import Tobermore build-up data from "TOB MASTER" worksheet
          if (workbook.SheetNames.includes("TOB MASTER")) {
            const worksheet = workbook.Sheets["TOB MASTER"];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: true,
              blankrows: false,
            }) as any[][];
            const parsed = processTobMoreMasterData(jsonData, geoToberWallColumnHeaders);
            if (Object.keys(parsed).length > 0) {
              Object.assign(geoToberWallBuildUpDimensions, parsed);
              importedSheets++;
            }
          }

          // Import Walls Section Build Up data from "Brick&con wall section build up" worksheet
          const wallsBuildUpSheetName = workbook.SheetNames.find((name) =>
            name.toLowerCase().includes("brick") && name.toLowerCase().includes("wall section build up")
          );
          console.log("📋 Available sheet names:", workbook.SheetNames);
          console.log("📋 Walls build up sheet found:", wallsBuildUpSheetName);
          if (wallsBuildUpSheetName) {
            const worksheet = workbook.Sheets[wallsBuildUpSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: true,
              blankrows: false,
            }) as any[][];
            console.log("📋 Walls build up sheet rows:", jsonData.length);
            console.log("📋 First 3 rows:", jsonData.slice(0, 3));
            wallsBuildUpDimensions = processWallsBuildUpData(jsonData);
            console.log("📋 Parsed wall spec IDs:", Object.keys(wallsBuildUpDimensions));
            console.log("📋 Sample (grw450):", wallsBuildUpDimensions["grw450"]?.slice(0, 3));
            if (Object.keys(wallsBuildUpDimensions).length > 0) {
              importedSheets++;
            }
          }

          // Import Plot Drives data from "PLOT DRIVES QUANTS" worksheet
          if (workbook.SheetNames.includes("PLOT DRIVES QUANTS")) {
            const worksheet = workbook.Sheets["PLOT DRIVES QUANTS"];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: true,
              blankrows: false,
            }) as any[][];
            const plotDrivesResult = processPlotDrivesData(jsonData);
            plotDrivesData = plotDrivesResult.data;
            plotDrivesColumnHeaders = plotDrivesResult.columnHeaders;
            plotDrivesDimensionValues = plotDrivesResult.dimensionValues;
            if (plotDrivesData && plotDrivesData.length > 0) {
              importedSheets++;
            } else {
            }
          } else {
          }

          // Import Drainage data from "PLOT DRAINAGE QUANTS" worksheet
          if (workbook.SheetNames.includes("PLOT DRAINAGE QUANTS")) {
            const worksheet = workbook.Sheets["PLOT DRAINAGE QUANTS"];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: true,
              blankrows: false,
            }) as any[][];
            const drainageResult = processDrainageData(jsonData);
            drainageData = drainageResult.data;
            drainageColumnHeaders = drainageResult.columnHeaders;
            drainageDimensionValues = drainageResult.dimensionValues;
            if (drainageData && drainageData.length > 0) {
              importedSheets++;
            } else {
            }
          } else {
          }

          // Import Soil Management data from "GW C&F Summary -TBC" worksheet
          const gwSummarySheetName = workbook.SheetNames.find(
            (name) =>
              name.toLowerCase().includes("gw c&f summary") ||
              name.toLowerCase().includes("c&f summary")
          );
          if (gwSummarySheetName) {
            const gwSheet = workbook.Sheets[gwSummarySheetName];
            const gwRows = XLSX.utils.sheet_to_json(gwSheet, {
              header: 1,
              raw: true,
              blankrows: false,
            }) as any[][];

            // ─────────────────────────────────────────────────────────────
            // DEBUG: dump everything the sheet contains before mapping.
            // Remove these logs once Soil Management filling is wired up.
            // ─────────────────────────────────────────────────────────────
            console.groupCollapsed(
              `[Soil Management Import] Sheet "${gwSummarySheetName}" — ${gwRows.length} rows`,
            );
            console.log("Raw rows (array-of-arrays):", gwRows);
            console.table(
              gwRows.map((row, idx) => ({
                rowIndex: idx,
                "A (label)": row?.[0] ?? "",
                "B": row?.[1] ?? "",
                "C": row?.[2] ?? "",
                "D (Cut/Arisings)": row?.[3] ?? "",
                "E (Fill)": row?.[4] ?? "",
              })),
            );

            const rowMappings: {
              labels: string[];
              cutField: keyof SoilSummaryData;
              fillField?: keyof SoilSummaryData;
            }[] = [
              { labels: ["foundations c&f", "foundations cut"], cutField: "foundationsCutArisings", fillField: "foundationsFill" },
              { labels: ["slab paths", "slab/paths"], cutField: "slabPathsCut", fillField: "slabPathsFill" },
              { labels: ["gardens"], cutField: "gardensCut", fillField: "gardensFill" },
              { labels: ["drives"], cutField: "drivesCut", fillField: "drivesFill" },
              { labels: ["foundations arisings"], cutField: "foundationsArisingsCut", fillField: "foundationsArisingsFill" },
              { labels: ["plot drainage"], cutField: "plotDrainageCut", fillField: "plotDrainageFill" },
              { labels: ["walls"], cutField: "wallsCut", fillField: "wallsFill" },
              { labels: ["plot services"], cutField: "plotServicesCut", fillField: "plotServicesFill" },
            ];

            const parsed: SoilSummaryData = {
              foundationsCutArisings: 0, foundationsFill: 0,
              slabPathsCut: 0, slabPathsFill: 0,
              gardensCut: 0, gardensFill: 0,
              drivesCut: 0, drivesFill: 0,
              foundationsArisingsCut: 0, foundationsArisingsFill: 0,
              plotDrainageCut: 0, plotDrainageFill: 0,
              wallsCut: 0, wallsFill: 0,
              plotServicesCut: 0, plotServicesFill: 0,
            };

            const matchedRows: Array<{ rowIndex: number; label: string; mappedTo: string; cut: number; fill: number }> = [];
            const skippedRows: Array<{ rowIndex: number; label: string; reason: string }> = [];

            for (let i = 0; i < gwRows.length; i++) {
              const row = gwRows[i];
              if (!row || row.length === 0) {
                skippedRows.push({ rowIndex: i, label: "", reason: "empty row" });
                continue;
              }
              // Label is in col A (index 0); Cut/Arisings in col D (index 3); Fill in col E (index 4)
              const label = String(row[0] || "").trim().toLowerCase();
              if (!label) {
                skippedRows.push({ rowIndex: i, label: "", reason: "no label" });
                continue;
              }
              if (label.includes("pos")) {
                skippedRows.push({ rowIndex: i, label, reason: "POS (excluded by design)" });
                continue;
              }
              let matched = false;
              for (const mapping of rowMappings) {
                if (mapping.labels.some((l) => label.includes(l))) {
                  const cutVal = Math.round((Number(row[3]) || 0) * 1000) / 1000;
                  const fillVal = mapping.fillField
                    ? Math.round((Number(row[4]) || 0) * 1000) / 1000
                    : 0;
                  parsed[mapping.cutField] = cutVal;
                  if (mapping.fillField) {
                    parsed[mapping.fillField] = fillVal;
                  }
                  matchedRows.push({
                    rowIndex: i,
                    label,
                    mappedTo: mapping.fillField
                      ? `${String(mapping.cutField)} / ${String(mapping.fillField)}`
                      : String(mapping.cutField),
                    cut: cutVal,
                    fill: fillVal,
                  });
                  matched = true;
                  break;
                }
              }
              if (!matched) {
                skippedRows.push({ rowIndex: i, label, reason: "no mapping matched" });
              }
            }

            console.log("Matched rows:");
            console.table(matchedRows);
            console.log("Skipped rows:");
            console.table(skippedRows);
            console.log("Parsed soilManagementData object:", parsed);
            console.groupEnd();

            soilManagementData = parsed;
          }
            const updatedGroundworks = {
              ...currentGroundworks,
            };

            if (cutFillsData.length > 0) {
              updatedGroundworks.cutFillsTable = cutFillsData;
            }

            // Save Cut & Fills prefix values if they were extracted
            if (Object.keys(cutFillsPrefixValues).length > 0) {
              (updatedGroundworks as any).cutFillsPrefixValues =
                cutFillsPrefixValues;
            }

            if (foundationsData.length > 0) {
              updatedGroundworks.foundationsTable = foundationsData;
            }

            // Save Foundations prefix values if they were extracted
            if (Object.keys(foundationsPrefixValues).length > 0) {
              let prefixValuesToStore = { ...foundationsPrefixValues };

              // Extract mesh reinforcement type if present
              if (foundationsPrefixValues.meshReinforcement) {
                const meshType = foundationsPrefixValues.meshReinforcement;
                // Store just the mesh type code (e.g., "A142", "B503")
                updatedGroundworks.meshReinforcementType = meshType;
                // Remove from prefix values since it has its own field
                const { meshReinforcement, ...remaining } = prefixValuesToStore;
                prefixValuesToStore = remaining;
              }

              // Extract substructure block type if present
              if (foundationsPrefixValues.substructureBlockType) {
                const blockType = foundationsPrefixValues.substructureBlockType;
                // Store the block type (e.g., "Thermalite", "Toplite")
                (updatedGroundworks as any).substructureBlockType = blockType;
                // Remove from prefix values since it has its own field
                const { substructureBlockType, ...remaining } =
                  prefixValuesToStore;
                prefixValuesToStore = remaining;
              }

              // Store remaining prefix values
              (updatedGroundworks as any).foundationsPrefixValues =
                prefixValuesToStore;
            }

            if (gardenData.length > 0) {
              updatedGroundworks.gardenTable = gardenData;
            }

            // Save Garden headers and dimension values if they were extracted
            if (Object.keys(gardenColumnHeaders).length > 0) {
              (updatedGroundworks as any).gardenColumnHeaders =
                gardenColumnHeaders;
            }

            if (Object.keys(gardenDimensionValues).length > 0) {
              (updatedGroundworks as any).gardenDimensionValues =
                gardenDimensionValues;
            }

            if (geoToberWallData.length > 0) {
              updatedGroundworks.geoToberWallTable = geoToberWallData;
            }

            if (Object.keys(geoToberWallColumnHeaders).length > 0) {
              (updatedGroundworks as any).geoToberWallColumnHeaders = geoToberWallColumnHeaders;
            }

            if (Object.keys(geoToberWallDimensionValues).length > 0) {
              (updatedGroundworks as any).geoToberWallDimensionValues = geoToberWallDimensionValues;
            }

            if (Object.keys(geoToberWallBuildUpDimensions).length > 0) {
              (updatedGroundworks as any).geoToberWallBuildUpDimensions = geoToberWallBuildUpDimensions;
            }

            if (plotPavingsData.length > 0) {
              updatedGroundworks.plotPavingsTable = plotPavingsData;
            }

            // Save Plot Pavings headers and dimension values as a full replace —
            // gated on plotPavingsData.length (the Plot Pavings sheet was actually
            // found and parsed), not on the headers/dimensionValues themselves
            // being non-empty. Gating on the latter meant a sheet whose 8
            // Configure-Headers-eligible fields didn't validate against their
            // dropdown options (see processPlotPavingsData) produced an empty
            // `{}` that this `> 0` check then skipped entirely — silently
            // leaving stale headers from a previous import/session in place
            // instead of clearing them, the opposite of "full replace".
            if (plotPavingsData.length > 0) {
              (updatedGroundworks as any).plotPavingsColumnHeaders = plotPavingsColumnHeaders;
              (updatedGroundworks as any).plotPavingsDimensionValues = plotPavingsDimensionValues;
            }

            if (scaffoldData.length > 0) {
              updatedGroundworks.scaffoldTable = scaffoldData;
            }

            // Save Scaffold headers and dimension values if they were extracted
            if (Object.keys(scaffoldColumnHeaders).length > 0) {
              (updatedGroundworks as any).scaffoldColumnHeaders =
                scaffoldColumnHeaders;
            }

            if (Object.keys(scaffoldDimensionValues).length > 0) {
              (updatedGroundworks as any).scaffoldDimensionValues =
                scaffoldDimensionValues;
            }

            if (plotServiceData.length > 0) {
              updatedGroundworks.plotServiceTable = plotServiceData;
            }

            if (retScreenWallsData.length > 0) {
              updatedGroundworks.retainingWallsTable = retScreenWallsData;
            }

            if (Object.keys(wallsBuildUpDimensions).length > 0) {
              (updatedGroundworks as any).wallsBuildUpDimensions = wallsBuildUpDimensions;
            }

            if (plotDrivesData.length > 0) {
              updatedGroundworks.plotDrivesTable = plotDrivesData;
            }

            // Save Plot Drives headers and dimension values as a full replace (not
            // merged with whatever was already configured), so a fresh import
            // always reflects only this sheet: a field this sheet doesn't have,
            // or couldn't validate against its own dropdown options (see
            // processPlotDrivesData), ends up unconfigured rather than silently
            // carrying over a stale value from a previous import/session.
            // Gated on plotDrivesData.length (the Plot Drives sheet was actually
            // found and parsed), not on the headers/dimensionValues themselves
            // being non-empty — gating on the latter meant a sheet whose fields
            // didn't validate against their dropdown options produced an empty
            // `{}` that an `Object.keys(...).length > 0` check then skipped
            // entirely, leaving stale headers in place instead of clearing them
            // (the opposite of "full replace"). A workbook without a Plot Drives
            // sheet at all (e.g. importing only other domains) still leaves an
            // already-configured tender alone, since plotDrivesData stays empty.
            if (plotDrivesData.length > 0) {
              (updatedGroundworks as any).plotDrivesColumnHeaders = plotDrivesColumnHeaders;
              (updatedGroundworks as any).plotDrivesDimensionValues = plotDrivesDimensionValues;
            }

            if (drainageData.length > 0) {
              updatedGroundworks.drainageTableStandard = drainageData;
            }

            if (soilManagementData) {
              (updatedGroundworks as any).soilManagementData = soilManagementData;
            }

            // Save Drainage dynamic column headers if extracted
            if (Object.keys(drainageColumnHeaders).length > 0) {
              updatedGroundworks.drainageColumnHeaders = drainageColumnHeaders;
            }
            if (Object.keys(drainageDimensionValues).length > 0) {
              updatedGroundworks.drainageDimensionValues = {
                ...(updatedGroundworks.drainageDimensionValues || {}),
                ...drainageDimensionValues,
              };
            }
            const polypipeConfig: Record<string, string> = {};
            if (drainageDimensionValues["polypipeNrPlotsTakeOff"]) polypipeConfig.nrPlotsTakeOff = drainageDimensionValues["polypipeNrPlotsTakeOff"];
            if (drainageDimensionValues["polypipeTotalPlots"]) polypipeConfig.totalPlots = drainageDimensionValues["polypipeTotalPlots"];
            if (Object.keys(polypipeConfig).length > 0) {
              (updatedGroundworks as any).drainagePolypipeConfig = {
                ...((updatedGroundworks as any).drainagePolypipeConfig || {}),
                ...polypipeConfig,
              };
            }

            resolve({
              groundworks: updatedGroundworks,
              importedSheets,
              hasWallBuildUpData:
                Object.keys(wallsBuildUpDimensions).length > 0 ||
                Object.keys(geoToberWallBuildUpDimensions).length > 0,
              pageChanges: [
                ...(cutFillsData.length > 0 ? [{ page: "cut-fills", count: cutFillsData.length }] : []),
                ...(foundationsData.length > 0 ? [{ page: "foundations-new", count: foundationsData.length }] : []),
                ...(drainageData.length > 0 ? [{ page: "drainage", count: drainageData.length }] : []),
                ...(plotDrivesData.length > 0 ? [{ page: "plot-drives", count: plotDrivesData.length }] : []),
                ...(plotServiceData.length > 0 ? [{ page: "plot-service", count: plotServiceData.length }] : []),
                ...(plotPavingsData.length > 0 ? [{ page: "plot-pavings", count: plotPavingsData.length }] : []),
                ...(scaffoldData.length > 0 ? [{ page: "scaffold", count: scaffoldData.length }] : []),
                ...(gardenData.length > 0 ? [{ page: "garden", count: gardenData.length }] : []),
                ...(retScreenWallsData.length > 0 ? [{ page: "retaining-walls", count: retScreenWallsData.length }] : []),
                ...(geoToberWallData.length > 0 ? [{ page: "geo-tober-wall", count: geoToberWallData.length }] : []),
              ],
            });
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsArrayBuffer(file);
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    })();
  });
}

  const processCutFillsData = (jsonData: any[][]): { data: any[]; prefixValues: Record<string, string> } => {
    // Find header row - look for "Plot No"
    let headerRowIndex = -1;
    for (let i = 0; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (
        row.some(
          (cell) =>
            String(cell).includes("Plot") && String(cell).includes("No"),
        )
      ) {
        headerRowIndex = i;
        break;
      }
    }
    if (headerRowIndex === -1) {
      console.error("Could not find header row with 'Plot No'");
      return { data: [], prefixValues: {} };
    }

    const headers = jsonData[headerRowIndex];

    // Extract prefix values from headers
    const extractedPrefixValues: Record<string, string> = {};
    const prefixFields = [
      {
        fieldKey: "topsoilStrip",
        baseText: "Excavate to strip topsoil, depth n.e.",
      },
    ];

    // Search header row and subsequent sub-header rows (up to 4 rows below)
    // Excel has multi-row headers: Row 3 = "Plot No", Row 4 = group headers, Row 5 = detailed column titles
    const headerRowsToSearch: any[][] = [];
    for (let r = 0; r <= 4 && headerRowIndex + r < jsonData.length; r++) {
      if (jsonData[headerRowIndex + r]) {
        headerRowsToSearch.push(jsonData[headerRowIndex + r]);
      }
    }

    prefixFields.forEach(({ fieldKey, baseText }) => {
      let found = false;
      for (const headerRow of headerRowsToSearch) {
        if (found) break;
        for (let columnIndex = 0; columnIndex < headerRow.length; columnIndex++) {
          const headerValue = String(headerRow[columnIndex] || "").trim();

          if (headerValue.toLowerCase().includes(baseText.toLowerCase())) {
            const escapedBaseText = baseText.replace(
              /[.*+?^${}()|[\]\\]/g,
              "\\$&"
            );
            const suffixMatch = headerValue.match(
              new RegExp(`${escapedBaseText}\\s*(.+)`, "i")
            );

            if (suffixMatch && suffixMatch[1]) {
              let extractedValue = suffixMatch[1].trim();
              extractedValue = extractedValue
                .replace(/\s*\(.*?\)\s*/g, "")
                .replace(/\s*(m[²³]|m2|m3)\s*$/i, "")
                .trim();
              const numericMatch = extractedValue.match(/^(\d+(?:\.\d+)?)/);
              if (numericMatch) {
                extractedPrefixValues[fieldKey] = numericMatch[1];
              }
            }
            found = true;
            break;
          }
        }
      }
    });

    // Get all rows after header
    const allDataRows = jsonData.slice(headerRowIndex + 1);
    // Find where actual data starts
    let dataStartIndex = 0;
    let foundDataStart = false;

    for (let i = 0; i < allDataRows.length; i++) {
      const row = allDataRows[i];
      if (row && row.length > 0) {
        const plotNoValue = String(row[0] || "").trim();
        const houseTypeValue = String(row[1] || "").trim();

        const hasValidPlotNo =
          plotNoValue &&
          plotNoValue !== "" &&
          !plotNoValue.toLowerCase().includes("total");
        const hasValidHouseType =
          houseTypeValue &&
          houseTypeValue !== "" &&
          !houseTypeValue.toLowerCase().includes("total") &&
          !houseTypeValue.toLowerCase().includes("sum") &&
          !houseTypeValue.toLowerCase().includes("note") &&
          isNaN(Number(houseTypeValue)) &&
          houseTypeValue.match(/[a-zA-Z]/);

        if (hasValidPlotNo && hasValidHouseType) {
          dataStartIndex = i;
          foundDataStart = true;
          break;
        }
      }
    }

    if (!foundDataStart) {
    }

    const dataRows = allDataRows.slice(dataStartIndex);
    // Map to CutFillRow objects using POSITION-BASED mapping.
    // Detect "GARAGES" separator row to tag rows as plot or garage — same
    // pattern as `processFoundationsData` so the plot/garage toggle on the
    // Cut & Fills page can filter rows consistently with Foundations.
    let isGarageSection = false;
    const allImportedRows: any[] = [];
    const nonEmptyDataRows = dataRows.filter(
      (row) =>
        row &&
        row.length > 0 &&
        row.some(
          (cell) => cell !== null && cell !== undefined && cell !== "",
        ),
    );
    nonEmptyDataRows.forEach((row, index) => {
      const firstCell = String(row[0] || "").trim().toUpperCase();
      if (firstCell === "GARAGES" || firstCell === "GARAGE") {
        isGarageSection = true;
        return; // Skip the separator row
      }
      const mapped = {
        key: `imported_${Date.now()}_${index}`,
        plotNo: String(row[0] || ""),
        housetype: String(row[1] || ""),
        foundationType: String(row[2] || ""),
        plotType: isGarageSection ? "garage" : "plot",
        vegStrip: Math.round((Number(row[3]) || 0) * 1000) / 1000,
        topsoilStrip: Math.round((Number(row[4]) || 0) * 1000) / 1000,
        topsoilDisposalOnsite: Math.round((Number(row[5]) || 0) * 1000) / 1000,
        topsoilDisposalOffsite: Math.round((Number(row[6]) || 0) * 1000) / 1000,
        cutFounds: Math.round((Number(row[7]) || 0) * 1000) / 1000,
        fillFounds: Math.round((Number(row[8]) || 0) * 1000) / 1000,
        cutPaths: Math.round((Number(row[9]) || 0) * 1000) / 1000,
        fillPaths: Math.round((Number(row[10]) || 0) * 1000) / 1000,
        cutGarden: Math.round((Number(row[11]) || 0) * 1000) / 1000,
        fillGarden: Math.round((Number(row[12]) || 0) * 1000) / 1000,
        cutDrives: Math.round((Number(row[13]) || 0) * 1000) / 1000,
        fillDrives: Math.round((Number(row[14]) || 0) * 1000) / 1000,
        fillToMakeUp6F2: Math.round((Number(row[15]) || 0) * 1000) / 1000,
      };
      if (
        mapped.plotNo ||
        mapped.vegStrip > 0 ||
        mapped.topsoilStrip > 0 ||
        mapped.cutFounds > 0 ||
        mapped.fillFounds > 0
      ) {
        allImportedRows.push(mapped);
      }
    });

    // Filter out invalid rows
    const importedRows = allImportedRows.filter((row) => {
      const plotNoStr = String(row.plotNo || "").trim();
      const housetypeStr = String(row.housetype || "").trim();

      const hasValidPlotNo =
        plotNoStr !== "" &&
        plotNoStr !== "0" &&
        !plotNoStr.toLowerCase().includes("total") &&
        !plotNoStr.toLowerCase().includes("sum");
      const hasValidHouseType =
        housetypeStr !== "" &&
        !housetypeStr.toLowerCase().includes("total") &&
        !housetypeStr.toLowerCase().includes("sum") &&
        isNaN(Number(housetypeStr)) &&
        housetypeStr.match(/[a-zA-Z]/);

      return hasValidPlotNo && hasValidHouseType;
    });
    if (importedRows.length > 0) {
    }

    return { data: importedRows, prefixValues: extractedPrefixValues };
  };

  // Helper function to process Foundations data - EXACT SAME LOGIC AS FoundationsNew.tsx
  const processFoundationsData = (jsonData: any[][]) => {
    // Find header row - look for "Plot No"
    let headerRowIndex = -1;
    for (let i = 0; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (
        row.some(
          (cell) =>
            String(cell).includes("Plot") && String(cell).includes("No"),
        )
      ) {
        headerRowIndex = i;
        break;
      }
    }
    if (headerRowIndex === -1) {
      console.error("Could not find header row with 'Plot No'");
      return { data: [], prefixValues: {} };
    }

    const headers = jsonData[headerRowIndex];

    // Search header row and subsequent sub-header rows (up to 4 rows below)
    const headerRowsToSearch: any[][] = [];
    for (let r = 0; r <= 4 && headerRowIndex + r < jsonData.length; r++) {
      if (jsonData[headerRowIndex + r]) {
        headerRowsToSearch.push(jsonData[headerRowIndex + r]);
      }
    }

    // Extract prefix values from headers (text before "Concrete to Strip foundation", etc.)
    const extractedPrefixValues: Record<string, string> = {};
    // Map field keys to their base text patterns
    const prefixFields = [
      {
        fieldKey: "gen1ConcreteToStrip",
        baseText: "Concrete to Strip foundation",
      },
      {
        fieldKey: "gen1ConcreteToStrip",
        baseText: "Concrete in foundation",
      },
      {
        fieldKey: "rc35ConcreteToPiled",
        baseText: "Concrete to Piled foundation",
      },
      {
        fieldKey: "rc35ConcreteToVibro",
        baseText: "Concrete to Vibro foundation",
      },
      {
        fieldKey: "gen1AdditionalConcrete",
        baseText: "in additional concrete",
      },
    ];

    // Search through all header rows to find matching columns
    prefixFields.forEach(({ fieldKey, baseText }) => {
      if (extractedPrefixValues[fieldKey]) return; // already matched by a previous pattern
      let found = false;
      for (const headerRow of headerRowsToSearch) {
        if (found) break;
        for (
          let columnIndex = 0;
          columnIndex < headerRow.length;
          columnIndex++
        ) {
          const headerValue = String(headerRow[columnIndex] || "").trim();

          if (headerValue.toLowerCase().includes(baseText.toLowerCase())) {
            const escapedBaseText = baseText.replace(
              /[.*+?^${}()|[\]\\]/g,
              "\\$&",
            );
            const prefixMatch = headerValue.match(
              new RegExp(`^(.+?)\\s*${escapedBaseText}`, "i"),
            );

            if (prefixMatch && prefixMatch[1]) {
              let extractedPrefix = prefixMatch[1].trim();

              const normalized = extractedPrefix
                .toUpperCase()
                .replace(/\s+/g, "");

              if (
                normalized === "RC28" ||
                normalized === "RC35" ||
                normalized === "RC28/35"
              ) {
                extractedPrefix = "RC28/35";
              } else if (
                normalized === "RC32" ||
                normalized === "RC40" ||
                normalized === "RC32/40"
              ) {
                extractedPrefix = "RC32/40";
              } else if (normalized.startsWith("GEN")) {
                extractedPrefix = normalized;
              } else if (normalized.startsWith("FND")) {
                if (normalized.endsWith("Z") && normalized.length > 4) {
                  extractedPrefix = normalized.slice(0, -1) + "z";
                } else {
                  extractedPrefix = normalized;
                }
              }

              // Only store the prefix if it is a recognised option.
              // Unrecognised values (e.g. "RC95") are silently discarded so
              // the column header falls back to the "xx" placeholder instead
              // of displaying an invalid concrete-type label.
              if (CONCRETE_STRIP_CODE_MAP[extractedPrefix]) {
                extractedPrefixValues[fieldKey] = extractedPrefix;
              }
              found = true;
              break;
            }
          }
        }
      }
    });

    // Extract mesh-type prefix values from headers.
    // Each "mesh" column in the export has the form "{MeshType} {ColumnLabel}"
    // e.g. "A393 Mesh to Floor m²", "A252 Top Layer Mesh m²".
    // We scan ALL header cells that contain "mesh" and classify them by the
    // column label so each field gets its own value independently.
    const supportedMeshTypes = [
      "A142", "A193", "A252", "A393",
      "B283", "B385", "B503", "B785", "B1131",
    ];

    // Helper: extract the first recognised mesh type from a header string
    const extractMeshType = (header: string): string | null => {
      const lower = header.toLowerCase();
      for (const t of supportedMeshTypes) {
        if (lower.includes(t.toLowerCase())) return t;
      }
      return null;
    };

    for (const headerRow of headerRowsToSearch) {
      for (let columnIndex = 0; columnIndex < headerRow.length; columnIndex++) {
        const headerValue = String(headerRow[columnIndex] || "").trim();
        const headerLower = headerValue.toLowerCase();
        if (!headerLower.includes("mesh")) continue;

        const meshType = extractMeshType(headerValue);
        if (!meshType) continue;

        // Classify by column label (most-specific patterns first)
        if (headerLower.includes("mesh to garage floor") || headerLower.includes("mesh to garage")) {
          if (!extractedPrefixValues.meshToGarageFloor) {
            extractedPrefixValues.meshToGarageFloor = meshType;
          }
        } else if (headerLower.includes("mesh to floor") || headerLower.includes("mesh to slab")) {
          if (!extractedPrefixValues.meshToFloor) {
            extractedPrefixValues.meshToFloor = meshType;
          }
        } else if (headerLower.includes("top layer mesh") || headerLower.includes("top layer")) {
          if (!extractedPrefixValues.topLayerMesh) {
            extractedPrefixValues.topLayerMesh = meshType;
          }
        } else {
          // Generic mesh reinforcement column
          if (!extractedPrefixValues.meshReinforcement) {
            extractedPrefixValues.meshReinforcement = meshType;
          }
        }
      }
    }

    // Extract substructure block type from headers
    const supportedBlockTypes = [
      "Thermalite",
      "Toplite",
      "Celcon - general",
      "Celcon - Barratt / Redrow deal",
      "Celcon",
    ];
    const blockTypeKeywords = ["block", "blockwork", "coursing brick"];

    for (const headerRow of headerRowsToSearch) {
      let blockFound = false;
      for (
        let columnIndex = 0;
        columnIndex < headerRow.length;
        columnIndex++
      ) {
        const headerValue = String(headerRow[columnIndex] || "").trim();
        const headerLower = headerValue.toLowerCase();

        const hasBlockKeyword = blockTypeKeywords.some((keyword) =>
          headerLower.includes(keyword),
        );

        if (hasBlockKeyword) {
          if (headerValue.includes(" - ")) {
            const dashIndex = headerValue.indexOf(" - ");
            const blockTypeAfterDash = headerValue
              .substring(dashIndex + 3)
              .trim();

            const matchedBlockType = supportedBlockTypes.find(
              (bt) => blockTypeAfterDash.toLowerCase() === bt.toLowerCase(),
            );

            if (matchedBlockType) {
              extractedPrefixValues.substructureBlockType = matchedBlockType;
              blockFound = true;
              break;
            } else {
              extractedPrefixValues.substructureBlockType = blockTypeAfterDash;
              blockFound = true;
              break;
            }
          }

          // Pattern 2: "Thermalite 100mm Aerated Blockwork" or "4N Toplite Block 300mm Wide"
          if (!blockFound) {
            for (const blockType of supportedBlockTypes) {
              if (headerLower.includes(blockType.toLowerCase())) {
                extractedPrefixValues.substructureBlockType = blockType;
                blockFound = true;
                break;
              }
            }
          }

          if (blockFound) break;
        }
      }
      if (blockFound) break;
    }

    // Extract B&B floor House and Garage GWALPF codes from headers.
    // House and garage use COMPLETELY DIFFERENT code sets:
    //   House:  GWALPF005/007-010/014-015/017-021 (beam & block, Jetfloor, etc.)
    //   Garage: GWALPF006 ("Garage - Beam and block") or GWALPF012 ("Cast Insitu Slab")
    // They must be matched separately — neither GWALPF006 nor GWALPF012 is in the
    // house list, and "Cast Insitu Slab" contains neither "b&b" nor "beam".

    // House B&B floor codes
    const bbFloorHouseCodes: string[] = [
      "GWALPF005", "GWALPF007", "GWALPF008", "GWALPF009", "GWALPF010",
      "GWALPF014", "GWALPF015", "GWALPF017", "GWALPF018", "GWALPF019",
      "GWALPF020", "GWALPF021",
    ];
    // Garage B&B floor codes (only two options, from Configure modal)
    const bbFloorGarageCodes: string[] = ["GWALPF006", "GWALPF012"];

    const matchCodeByLabel = (header: string, codes: string[]): string | null => {
      const headerLower = header.toLowerCase();
      for (const code of codes) {
        const codeLabel = EVALUATE_CODES[code as EvaluateCode]?.label?.toLowerCase();
        // Only match if the header equals or contains the full code label.
        // The reverse condition (codeLabel contains headerLower) is intentionally
        // omitted — it matches single words like "block" or "beam" against
        // "Garage - Beam and block", causing false positives from substructure
        // section group headers that appear before the actual garage floor column.
        if (codeLabel && (codeLabel === headerLower || headerLower.includes(codeLabel))) {
          return code;
        }
      }
      return null;
    };

    // Extract bbFloorGarage first (unique codes that don't overlap with house codes)
    for (const headerRow of headerRowsToSearch) {
      if (extractedPrefixValues.bbFloorGarage) break;
      for (let columnIndex = 0; columnIndex < headerRow.length; columnIndex++) {
        const headerValue = String(headerRow[columnIndex] || "").trim();
        const code = matchCodeByLabel(headerValue, bbFloorGarageCodes);
        if (code) {
          extractedPrefixValues.bbFloorGarage = code;
          break;
        }
      }
    }

    // Extract bbFloorHouse (headers must contain "b&b" or "beam" to filter house codes)
    for (const headerRow of headerRowsToSearch) {
      if (extractedPrefixValues.bbFloorHouse) break;
      for (let columnIndex = 0; columnIndex < headerRow.length; columnIndex++) {
        const headerValue = String(headerRow[columnIndex] || "").trim();
        const headerLower = headerValue.toLowerCase();
        if (!headerLower.includes("b&b") && !headerLower.includes("beam")) continue;
        const code = matchCodeByLabel(headerValue, bbFloorHouseCodes);
        if (code) {
          extractedPrefixValues.bbFloorHouse = code;
          break;
        }
      }
    }

    // Extract piling mat dimension from headers (e.g., "150mm thick recycled 6F2 piling mat")
    for (const headerRow of headerRowsToSearch) {
      let pileMatFound = false;
      for (let columnIndex = 0; columnIndex < headerRow.length; columnIndex++) {
        const headerValue = String(headerRow[columnIndex] || "").trim();
        if (headerValue.toLowerCase().includes("piling mat")) {
          const numericMatch = headerValue.match(/^(\d+(?:\.\d+)?)/);
          if (numericMatch) {
            extractedPrefixValues.pileMat = numericMatch[1];
          }
          pileMatFound = true;
          break;
        }
      }
      if (pileMatFound) break;
    }

    // Extract concrete blinding thickness from headers (e.g., "75mm thick concrete blinding")
    for (const headerRow of headerRowsToSearch) {
      let concreteBlindingFound = false;
      for (let columnIndex = 0; columnIndex < headerRow.length; columnIndex++) {
        const headerValue = String(headerRow[columnIndex] || "").trim();
        if (headerValue.toLowerCase().includes("concrete blinding")) {
          const numericMatch = headerValue.match(/^(\d+(?:\.\d+)?)/);
          if (numericMatch) {
            extractedPrefixValues.concreteBlinding = numericMatch[1];
          }
          concreteBlindingFound = true;
          break;
        }
      }
      if (concreteBlindingFound) break;
    }

    // Extract Conc Trench, Conc Piled, Conc Vibro numeric values
    const concFields = ["Conc Trench", "Conc Piled", "Conc Vibro"];
    const concFieldKeys = ["concTrench", "concPiled", "concVibro"];
    const concColumnOffsets = [1, 2, 3]; // Conc Trench: +1, Conc Piled: +2, Conc Vibro: +3

    // Search through all rows to find these labels
    for (let rowIndex = 0; rowIndex < jsonData.length; rowIndex++) {
      const row = jsonData[rowIndex];
      if (!row || row.length === 0) continue;

      // Check all columns for the label
      for (let colIndex = 0; colIndex < row.length; colIndex++) {
        const cellValue = String(row[colIndex] || "").trim();

        concFields.forEach((label, idx) => {
          const fieldKey = concFieldKeys[idx];
          const columnOffset = concColumnOffsets[idx];

          // If we haven't found this field yet and the cell matches the label
          if (
            !extractedPrefixValues[fieldKey] &&
            cellValue.toLowerCase() === label.toLowerCase()
          ) {
            // Get the value from the appropriate column offset
            const valueColIndex = colIndex + columnOffset;
            if (valueColIndex < row.length) {
              const valueCell = row[valueColIndex];
              if (
                valueCell !== null &&
                valueCell !== undefined &&
                valueCell !== ""
              ) {
                const numericValue = Number(valueCell);
                if (!isNaN(numericValue)) {
                  extractedPrefixValues[fieldKey] = numericValue;
                } else {
                }
              }
            }
          }
        });
      }
    }

    // Extract Exc Trench, Exc Piled, Exc Vibro numeric values
    const excFields = ["Exc Trench", "Exc Piled", "Exc Vibro"];
    const excFieldKeys = ["excTrench", "excPiled", "excVibro"];
    const excColumnOffsets = [1, 2, 3]; // Exc Trench: +1, Exc Piled: +2, Exc Vibro: +3

    // Search through all rows to find these labels
    for (let rowIndex = 0; rowIndex < jsonData.length; rowIndex++) {
      const row = jsonData[rowIndex];
      if (!row || row.length === 0) continue;

      // Check all columns for the label
      for (let colIndex = 0; colIndex < row.length; colIndex++) {
        const cellValue = String(row[colIndex] || "").trim();

        excFields.forEach((label, idx) => {
          const fieldKey = excFieldKeys[idx];
          const columnOffset = excColumnOffsets[idx];

          // If we haven't found this field yet and the cell matches the label
          if (
            !extractedPrefixValues[fieldKey] &&
            cellValue.toLowerCase() === label.toLowerCase()
          ) {
            // Get the value from the appropriate column offset
            const valueColIndex = colIndex + columnOffset;
            if (valueColIndex < row.length) {
              const valueCell = row[valueColIndex];
              if (
                valueCell !== null &&
                valueCell !== undefined &&
                valueCell !== ""
              ) {
                const numericValue = Number(valueCell);
                if (!isNaN(numericValue)) {
                  extractedPrefixValues[fieldKey] = numericValue;
                } else {
                }
              }
            }
          }
        });
      }
    }
    const allDataRows = jsonData.slice(headerRowIndex + 1);
    // Smart detection for multiple potential skip rows
    let dataRowsToImport = allDataRows;
    let skipCount = 0;

    for (let i = 0; i < Math.min(3, allDataRows.length); i++) {
      const row = allDataRows[i];
      if (row && row.length > 0) {
        const firstCellValue = String(row[0] || "")
          .toLowerCase()
          .trim();

        const shouldSkip =
          firstCellValue.includes("total") ||
          firstCellValue.includes("sum") ||
          firstCellValue.includes("note") ||
          firstCellValue === "" ||
          (!isNaN(Number(firstCellValue)) &&
            firstCellValue !== "" &&
            !firstCellValue.match(/[a-zA-Z]/));

        if (shouldSkip) {
          skipCount++;
        } else {
          break;
        }
      }
    }

    dataRowsToImport = allDataRows.slice(skipCount);
    // Map to FoundationRow objects using POSITION-BASED mapping
    // Detect "GARAGES" separator row to tag rows as plot or garage
    let isGarageSection = false;
    const allImportedRows: any[] = [];
    const nonEmptyRows = dataRowsToImport.filter(
      (row) =>
        row &&
        row.length > 0 &&
        row.some(
          (cell) => cell !== null && cell !== undefined && cell !== "",
        ),
    );
    nonEmptyRows.forEach((row, index) => {
      const firstCell = String(row[0] || "").trim().toUpperCase();
      if (firstCell === "GARAGES" || firstCell === "GARAGE") {
        isGarageSection = true;
        return; // Skip the separator row
      }
      // Skip subtotal rows (e.g. "Garage Total", "Plot Total", "Total", "Sum")
      // that appear between the plots and garages sections.
      const firstCellLower = firstCell.toLowerCase();
      if (
        firstCellLower.includes("total") ||
        firstCellLower.includes("sum")
      ) {
        return;
      }
      // Skip merged column rows (section headers where only column 0 has data)
      if (row[0] !== null && row[0] !== undefined && row[0] !== "") {
        const allOtherEmpty = row
          .slice(1)
          .every(
            (cell: any) => cell === null || cell === undefined || cell === "",
          );
        if (allOtherEmpty) return;
      }
      const plotNo = String(row[0] || "");
      const housetype = String(row[1] || "");
      const hasPlotNo = plotNo && plotNo.trim() !== "" && plotNo !== "0";
      const housetypeStr = housetype.trim();
      const hasValidHouseType =
        housetypeStr !== "" &&
        !housetypeStr.toLowerCase().includes("total") &&
        !housetypeStr.toLowerCase().includes("sum") &&
        isNaN(Number(housetypeStr)) && // Not purely numeric
        housetypeStr.match(/[a-zA-Z]/); // Contains letters
      if (!hasPlotNo || !hasValidHouseType) return;

      allImportedRows.push({
        key: `imported_${Date.now()}_${index}`,
        plotNo,
        housetype,
        foundationType: String(row[2] || ""),
        plotType: isGarageSection ? "garage" : "plot",
        pileMat: Math.round((Number(row[3]) || 0) * 1000) / 1000,
        pileMatRemoval: Math.round((Number(row[4]) || 0) * 1000) / 1000,
        plotGarSetOut: Math.round((Number(row[5]) || 0) * 1000) / 1000,
        pileSetOut: Math.round((Number(row[6]) || 0) * 1000) / 1000,
        pileAttendance: Math.round((Number(row[7]) || 0) * 1000) / 1000,
        excavateTrench: Math.round((Number(row[8]) || 0) * 1000) / 1000,
        excavatePiled: Math.round((Number(row[9]) || 0) * 1000) / 1000,
        excavateVibro: Math.round((Number(row[10]) || 0) * 1000) / 1000,
        levelCompact: Math.round((Number(row[11]) || 0) * 1000) / 1000,
        stockpileArisings: Math.round((Number(row[12]) || 0) * 1000) / 1000,
        concreteBlinding: Math.round((Number(row[13]) || 0) * 1000) / 1000,
        formplasOrSimilar: Math.round((Number(row[14]) || 0) * 1000) / 1000,
        cropPiles: Math.round((Number(row[15]) || 0) * 1000) / 1000,
        fillSteelPiles: Math.round((Number(row[16]) || 0) * 1000) / 1000,
        rebar: Math.round((Number(row[17]) || 0) * 1000) / 1000,
        stepsInFounds: Math.round((Number(row[18]) || 0) * 1000) / 1000,
        meshReinforcement: Math.round((Number(row[19]) || 0) * 1000) / 1000,
        gen1ConcreteToStrip: Math.round((Number(row[20]) || 0) * 1000) / 1000,
        rc35ConcreteToPiled: Math.round((Number(row[21]) || 0) * 1000) / 1000,
        rc35ConcreteToVibro: Math.round((Number(row[22]) || 0) * 1000) / 1000,
        stripClaymaster: Math.round((Number(row[23]) || 0) * 1000) / 1000,
        piledClaymaster: Math.round((Number(row[24]) || 0) * 1000) / 1000,
        trench4NBlock300mmWide:
          Math.round((Number(row[25]) || 0) * 1000) / 1000,
        trench7NBlock300mmWide:
          Math.round((Number(row[26]) || 0) * 1000) / 1000,
        trench4NBlock355mmWide:
          Math.round((Number(row[27]) || 0) * 1000) / 1000,
        trench7NBlock355mmWide:
          Math.round((Number(row[28]) || 0) * 1000) / 1000,
        dense4NBlockwork100mm: Math.round((Number(row[29]) || 0) * 1000) / 1000,
        dense7NBlockwork100mm: Math.round((Number(row[30]) || 0) * 1000) / 1000,
        dense10NBlockwork100mm:
          Math.round((Number(row[31]) || 0) * 1000) / 1000,
        aerated4NBlockwork100mm:
          Math.round((Number(row[32]) || 0) * 1000) / 1000,
        aerated7NBlockwork100mm:
          Math.round((Number(row[33]) || 0) * 1000) / 1000,
        aerated4NBlockwork140mm:
          Math.round((Number(row[34]) || 0) * 1000) / 1000,
        aerated7NBlockwork140mm:
          Math.round((Number(row[35]) || 0) * 1000) / 1000,
        dense100mmCoursingBrick:
          Math.round((Number(row[36]) || 0) * 1000) / 1000,
        aerated100mmCoursingBrick:
          Math.round((Number(row[37]) || 0) * 1000) / 1000,
        aerated140mmCoursingBrick:
          Math.round((Number(row[38]) || 0) * 1000) / 1000,
        layFacingBrickwork: Math.round((Number(row[39]) || 0) * 1000) / 1000,
        supplyFacingBrickwork: Math.round((Number(row[40]) || 0) * 1000) / 1000,
        beamInfill: Math.round((Number(row[41]) || 0) * 1000) / 1000,
        formCavity: Math.round((Number(row[42]) || 0) * 1000) / 1000,
        cavityFill: Math.round((Number(row[43]) || 0) * 1000) / 1000,
        telescopicVents: Math.round((Number(row[44]) || 0) * 1000) / 1000,
        telescopicVents140: Math.round((Number(row[45]) || 0) * 1000) / 1000,
        perimeterInsulation: Math.round((Number(row[46]) || 0) * 1000) / 1000,
        perimeterInsulation30: Math.round((Number(row[47]) || 0) * 1000) / 1000,
        dpc100mm: Math.round((Number(row[48]) || 0) * 1000) / 1000,
        backfillExcavations: Math.round((Number(row[49]) || 0) * 1000) / 1000,
        cavityInsulation: Math.round((Number(row[50]) || 0) * 1000) / 1000,
        additionalExcavation: Math.round((Number(row[51]) || 0) * 1000) / 1000,
        gen1AdditionalConcrete:
          Math.round((Number(row[52]) || 0) * 1000) / 1000,
        additional300mmTrenchBlock:
          Math.round((Number(row[53]) || 0) * 1000) / 1000,
        additional355mmTrenchBlock:
          Math.round((Number(row[54]) || 0) * 1000) / 1000,
        additional100mmBlockwork:
          Math.round((Number(row[55]) || 0) * 1000) / 1000,
        backfillExcavationsAsDug:
          Math.round((Number(row[56]) || 0) * 1000) / 1000,
        depositOnSite: Math.round((Number(row[57]) || 0) * 1000) / 1000,
        eoExposedBrickwork: Math.round((Number(row[58]) || 0) * 1000) / 1000,
        eoTanking: Math.round((Number(row[59]) || 0) * 1000) / 1000,
        depositCFAArisings: Math.round((Number(row[60]) || 0) * 1000) / 1000,
        pileCollar: Math.round((Number(row[61]) || 0) * 1000) / 1000,
        cordekCellcore155: Math.round((Number(row[62]) || 0) * 1000) / 1000,
        cordekCellcore225: Math.round((Number(row[63]) || 0) * 1000) / 1000,
        bbFloorHouse: Math.round((Number(row[65]) || 0) * 1000) / 1000,
        layOnlyFloors: Math.round((Number(row[66]) || 0) * 1000) / 1000,
        bbFloorGarage: Math.round((Number(row[67]) || 0) * 1000) / 1000,
        insituFloorHouse: Math.round((Number(row[68]) || 0) * 1000) / 1000,
        topHats: Math.round((Number(row[69]) || 0) * 1000) / 1000,
        eo2000gDPM: Math.round((Number(row[70]) || 0) * 1000) / 1000,
        eoAmber1DPM: Math.round((Number(row[71]) || 0) * 1000) / 1000,
        eoAmber2DPM: Math.round((Number(row[72]) || 0) * 1000) / 1000,
        radonMembrane: Math.round((Number(row[73]) || 0) * 1000) / 1000,
        gasMembrane: Math.round((Number(row[74]) || 0) * 1000) / 1000,
        meshToFloor: Math.round((Number(row[75]) || 0) * 1000) / 1000,
        topLayerMesh: Math.round((Number(row[76]) || 0) * 1000) / 1000,
        insituFloorGarage: Math.round((Number(row[77]) || 0) * 1000) / 1000,
        meshToGarageFloor: Math.round((Number(row[78]) || 0) * 1000) / 1000,
        raft: Math.round((Number(row[79]) || 0) * 1000) / 1000,
      });
    });
    if (allImportedRows.length > 0) {
    }

    return {
      data: allImportedRows,
      prefixValues: extractedPrefixValues,
    };
  };

  // Helper function to process Garden data - exact logic from Garden.tsx
  const processGardenData = (jsonData: any[][]) => {
    // Define excel columns for Garden (same as in Garden.tsx)
    const excelColumns = [
      { key: "plotNo", title: "Plot No" },
      { key: "housetype", title: "House Type" },
      { key: "scaffoldPlatform", title: "Scaffold Platform (m²)" },
      { key: "thickNoDigLayer", title: "xxxmm thick 'no dig' layer (m²)" },
      { key: "geotextileLayer", title: "Geotextile Layer (m²)" },
      {
        key: "spreadLevelCleanSubsoilCapSiteWon",
        title: "Spread and level xxxmm thick site won clean subsoil capping layer (m²)",
      },
      {
        key: "spreadLevelCleanSubsoilCapImported",
        title: "Spread and level xxxmm thick imported clean subsoil capping layer (m²)",
      },
      { key: "harrowGardens", title: "Harrow gardens to 300mm depth (m²)" },
      { key: "plotClearance", title: "Remove general builders rubbish prior to topsoiling - if separate (m²)" },
      {
        key: "spreadLevelTopsoilSiteWon",
        title: "Spread and level xxxmm thick site won topsoil (m²)",
      },
      {
        key: "spreadLevelTopsoilImported",
        title: "Spread and level xxxmm thick imported topsoil (m²)",
      },
    ];
    let headerRowIndex = -1;
    for (let i = 0; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (
        row.some(
          (cell) =>
            String(cell).includes("Plot") && String(cell).includes("No"),
        )
      ) {
        headerRowIndex = i;
        break;
      }
    }
    if (headerRowIndex === -1) {
      console.error("Could not find header row with 'Plot No'");
      return { data: [], columnHeaders: {}, dimensionValues: {} };
    }

    const headers = jsonData[headerRowIndex];
    const allDataRows = jsonData.slice(headerRowIndex + 1);
    // Skip only the first row (totals row)
    const dataRows = allDataRows.slice(1);
    // Find basic column indices
    const plotNoIdx = headers.findIndex(
      (h: any) => String(h).includes("Plot") && String(h).includes("No"),
    );
    const housetypeIdx = headers.findIndex(
      (h: any) =>
        String(h).toLowerCase().includes("house") &&
        String(h).toLowerCase().includes("type"),
    );

    // Find the 'no dig' layer column first as a reference point
    const noDigLayerIdx = headers.findIndex((h: any) => {
      const headerText = String(h).toLowerCase();
      return (
        headerText.includes("no dig") ||
        (headerText.includes("thick") && headerText.includes("layer"))
      );
    });
    if (noDigLayerIdx >= 0) {
    }

    let scaffoldPlatformIdx = -1; // Not in Excel, will remain -1

    // Direct findIndex fallbacks for renamed columns (backward compatibility with old Excel headers)
    const plotClearanceDirectIdx = headers.findIndex((h: any) => {
      const h2 = String(h).toLowerCase();
      return h2.includes("clearance") || h2.includes("rubbish");
    });
    const harrowDirectIdx = headers.findIndex((h: any) =>
      String(h).toLowerCase().includes("harrow"),
    );

    // Build column mapping using sequential search
    const columnMapping: { [key: string]: number } = {};
    let currentSearchIndex = 0;

    excelColumns.forEach((col, colArrayIndex) => {
      // For basic columns, use indices we already found
      if (colArrayIndex <= 3) {
        if (col.key === "plotNo") {
          columnMapping[col.key] = plotNoIdx;
        } else if (col.key === "housetype") {
          columnMapping[col.key] = housetypeIdx;
        } else if (col.key === "scaffoldPlatform") {
          // Skip - doesn't exist in Excel file
          columnMapping[col.key] = -1;
        } else if (col.key === "thickNoDigLayer") {
          columnMapping[col.key] = noDigLayerIdx;
        }
        if (columnMapping[col.key] >= 0) {
          currentSearchIndex = columnMapping[col.key] + 1;
        }
      } else {
        // For other columns, search sequentially from currentSearchIndex
        const foundIndex = headers.findIndex((h: any, idx: number) => {
          if (idx < currentSearchIndex) return false;

          const headerText = String(h || "")
            .toLowerCase()
            .trim();
          const titleText = col.title.toLowerCase();

          // Match based on the column title, flexible with dimensions
          if (/(x{2,}mm?)/i.test(col.title)) {
            // Dynamic column - match without dimension pattern
            const patternWithoutDimension = titleText
              .replace(/(x{2,}mm?)/gi, "")
              .replace(/\(.*?\)/g, "")
              .trim();
            const headerWithoutParens = headerText
              .replace(/\(.*?\)/g, "")
              .trim();

            const patternWords = patternWithoutDimension
              .split(/\s+/)
              .filter((w) => w.length > 0);
            const match = patternWords.every((word) =>
              headerWithoutParens.includes(word),
            );

            return match;
          } else {
            // Non-dynamic column - exact matching
            const titleWords = titleText
              .replace(/\(.*?\)/g, "")
              .trim()
              .split(/\s+/);
            return titleWords.every((word) => headerText.includes(word));
          }
        });

        if (foundIndex >= 0) {
          columnMapping[col.key] = foundIndex;
          currentSearchIndex = foundIndex + 1;
        } else if (col.key === "harrowGardens" && harrowDirectIdx >= 0) {
          // Fallback: match old "Harrow Gardens" or new "Harrow gardens to 300mm depth"
          columnMapping[col.key] = harrowDirectIdx;
          currentSearchIndex = harrowDirectIdx + 1;
        } else if (
          col.key === "plotClearance" &&
          plotClearanceDirectIdx >= 0
        ) {
          // Fallback: match old "Plot Clearance" or new "Remove general builders rubbish..."
          columnMapping[col.key] = plotClearanceDirectIdx;
          currentSearchIndex = plotClearanceDirectIdx + 1;
        }
      }
    });
    // Extract column headers and dimension values
    const extractedHeaders: Record<string, string> = {};
    const extractedDimensionValues: Record<string, string> = {};

    // Build multi-row headers to search (up to 4 rows below headerRowIndex)
    const headerRowsToSearch: string[][] = [];
    for (let r = headerRowIndex; r <= Math.min(headerRowIndex + 4, jsonData.length - 1); r++) {
      if (jsonData[r]) {
        headerRowsToSearch.push(jsonData[r].map((cell: any) => String(cell || "").trim()));
      }
    }

    // Define fixed text for each dynamic column to extract only the leading dimension
    const dimensionFixedText: Record<string, string> = {
      thickNoDigLayer: "thick",
      spreadLevelCleanSubsoilCapSiteWon: "thick site won clean subsoil capping layer",
      spreadLevelCleanSubsoilCapImported: "thick imported clean subsoil capping layer",
      spreadLevelTopsoilSiteWon: "thick site won topsoil",
      spreadLevelTopsoilImported: "thick imported topsoil",
    };

    excelColumns.forEach((col) => {
      const actualColumnIndex = columnMapping[col.key];
      if (actualColumnIndex !== undefined && actualColumnIndex >= 0) {
        const headerValue = String(headers[actualColumnIndex] || "").trim();
        extractedHeaders[col.key] = headerValue;

        // Check if this column has dimension patterns
        if (/(x{2,}mm?)/i.test(col.title)) {
          // Search across multiple header rows for the dimension value
          const fixedText = dimensionFixedText[col.key];
          let found = false;

          for (const headerRow of headerRowsToSearch) {
            if (found) break;
            const cellValue = headerRow[actualColumnIndex];
            if (!cellValue) continue;

            if (fixedText) {
              // Find the fixed text portion and extract the leading dimension before it
              const fixedTextIndex = cellValue.toLowerCase().indexOf(fixedText.toLowerCase());
              if (fixedTextIndex > 0) {
                const leadingText = cellValue.substring(0, fixedTextIndex).trim();
                // Extract numeric dimension (supports decimals like 12.34)
                const dimMatch = leadingText.match(/(\d+(?:\.\d+)?)\s*mm/i) || leadingText.match(/(\d+(?:\.\d+)?)\s*$/);
                if (dimMatch) {
                  extractedDimensionValues[col.key] = dimMatch[1];
                  found = true;
                }
              }
            }

            if (!found) {
              // Fallback: extract first number followed by mm
              const dimensionMatch = cellValue.match(/\b(\d+(?:\.\d+)?)\s*mm\b/i);
              if (dimensionMatch) {
                extractedDimensionValues[col.key] = dimensionMatch[1];
                found = true;
              }
            }
          }
        }
      }
    });
    // Get column indices from mapping (noDigLayerIdx already defined earlier)
    const geotextileIdx = columnMapping["geotextileLayer"] ?? -1;
    const cleanSubsoilSiteWonIdx =
      columnMapping["spreadLevelCleanSubsoilCapSiteWon"] ?? -1;
    const cleanSubsoilImportedIdx =
      columnMapping["spreadLevelCleanSubsoilCapImported"] ?? -1;
    const harrowIdx = columnMapping["harrowGardens"] ?? -1;
    const plotClearanceIdx = columnMapping["plotClearance"] ?? -1;
    const topsoilSiteWonIdx = columnMapping["spreadLevelTopsoilSiteWon"] ?? -1;
    const topsoilImportedIdx =
      columnMapping["spreadLevelTopsoilImported"] ?? -1;
    const allImportedRows = dataRows
      .filter(
        (row) =>
          row &&
          row.length > 0 &&
          row.some(
            (cell) => cell !== null && cell !== undefined && cell !== "",
          ),
      )
      .map((row, index) => {
        return {
          key: `imported_${Date.now()}_${index}`,
          plotNo: String(row[plotNoIdx] || ""),
          housetype: String(row[housetypeIdx] || ""),
          gardenArea: 0,
          scaffoldPlatform:
            Math.round(Number(row[scaffoldPlatformIdx] || 0) * 1000) / 1000,
          thickNoDigLayer:
            Math.round(Number(row[noDigLayerIdx] || 0) * 1000) / 1000,
          geotextileLayer:
            Math.round(Number(row[geotextileIdx] || 0) * 1000) / 1000,
          spreadLevelCleanSubsoilCapSiteWon:
            Math.round(Number(row[cleanSubsoilSiteWonIdx] || 0) * 1000) / 1000,
          spreadLevelCleanSubsoilCapImported:
            Math.round(Number(row[cleanSubsoilImportedIdx] || 0) * 1000) / 1000,
          harrowGardens: Math.round(Number(row[harrowIdx] || 0) * 1000) / 1000,
          plotClearance:
            Math.round(Number(row[plotClearanceIdx] || 0) * 1000) / 1000,
          spreadLevelTopsoilSiteWon:
            Math.round(Number(row[topsoilSiteWonIdx] || 0) * 1000) / 1000,
          spreadLevelTopsoilImported:
            Math.round(Number(row[topsoilImportedIdx] || 0) * 1000) / 1000,
        };
      })
      .filter((row) => {
        const plotNoStr = String(row.plotNo || "").trim();
        const hasPlotNo =
          plotNoStr !== "" &&
          plotNoStr !== "0" &&
          plotNoStr !== "null" &&
          plotNoStr !== "undefined";
        const housetypeStr = String(row.housetype || "").trim();
        const hasValidHouseType =
          housetypeStr !== "" &&
          !housetypeStr.toLowerCase().includes("total") &&
          !housetypeStr.toLowerCase().includes("sum") &&
          isNaN(Number(housetypeStr)) && // Not purely numeric
          housetypeStr.match(/[a-zA-Z]/); // Contains letters
        return hasPlotNo && hasValidHouseType;
      });
    if (allImportedRows.length === 0) {
    }

    return {
      data: allImportedRows,
      columnHeaders: extractedHeaders,
      dimensionValues: extractedDimensionValues,
    };
  };

  // Helper function to process Geowall & Tobermore Walls data
  const processGeoToberWallData = (jsonData: any[][]) => {
    if (!jsonData || jsonData.length === 0) {
      console.error("❌ jsonData is empty or undefined!");
      return [];
    }
    jsonData.slice(0, 3).forEach((row, i) => {});

    let headerRowIndex = -1;
    for (let i = 0; i < Math.min(jsonData.length, 15); i++) {
      const row = jsonData[i];

      if (row && Array.isArray(row)) {
        // Check each cell for Plot and No
        const cellsWithPlot = [];
        const cellsWithNo = [];
        const cellsWithBoth = [];

        row.forEach((cell, cellIndex) => {
          const cellStr = String(cell || "");
          if (cellStr.includes("Plot")) cellsWithPlot.push(cellIndex);
          if (cellStr.includes("No")) cellsWithNo.push(cellIndex);
          if (cellStr.includes("Plot") && cellStr.includes("No"))
            cellsWithBoth.push(cellIndex);
        });
        if (cellsWithBoth.length > 0) {
          headerRowIndex = i;
          break;
        }
      } else {
      }
    }
    if (headerRowIndex === -1) {
      console.error(
        "❌ Could not find header row with 'Plot' AND 'No' in the same cell",
      );
      console.error(
        "💡 The column header should be like 'Plot No' or 'Plot No.'",
      );
      console.error(
        "💡 Check if 'Plot' and 'No' are in separate cells or if the header is in a different format",
      );
      return [];
    }

    const headers = jsonData[headerRowIndex];
    const allDataRows = jsonData.slice(headerRowIndex + 1);
    // Find where actual data starts by looking for the first row with a valid house type
    let dataStartIndex = 0;
    let foundDataStart = false;

    for (let i = 0; i < allDataRows.length; i++) {
      const row = allDataRows[i];
      if (row && row.length > 0) {
        const plotNoValue = String(row[0] || "").trim();
        const houseTypeValue = String(row[1] || "").trim();

        // Look for first row that has both plot number and house type that look like actual data
        const hasValidPlotNo =
          plotNoValue &&
          plotNoValue !== "" &&
          !plotNoValue.toLowerCase().includes("total");
        const hasValidHouseType =
          houseTypeValue &&
          houseTypeValue !== "" &&
          !houseTypeValue.toLowerCase().includes("total") &&
          !houseTypeValue.toLowerCase().includes("sum") &&
          !houseTypeValue.toLowerCase().includes("note") &&
          isNaN(Number(houseTypeValue)) && // Not purely numeric
          houseTypeValue.match(/[a-zA-Z]/); // Contains letters

        if (hasValidPlotNo && hasValidHouseType) {
          dataStartIndex = i;
          foundDataStart = true;
          break;
        } else {
        }
      }
    }

    if (!foundDataStart) {
    }

    const dataRows = allDataRows.slice(dataStartIndex);
    // Enhanced column detection function
    const findColumnIndex = (searchTerms: string[]) => {
      return headers.findIndex((header: any) => {
        const headerStr = String(header || "")
          .toLowerCase()
          .trim();
        return searchTerms.some((term) =>
          headerStr.includes(term.toLowerCase()),
        );
      });
    };
    // Basic columns
    const plotNoIdx = findColumnIndex(["plot no", "plotno"]);
    const housetypeIdx = findColumnIndex(["house type", "housetype"]);

    // GEOWALLS section - comprehensive search terms
    const excavateTrenchAndTipOnSiteIdx = findColumnIndex([
      "excavate trench and tip on-site",
      "excavate trench and tip on site",
      "excavate trench",
      "tip on-site",
    ]);

    const compactFormationIdx = findColumnIndex(["compact formation"]);

    const concreteBlindingIdx = findColumnIndex(["concrete blinding"]);

    const a393MeshIdx = findColumnIndex(["a393 mesh"]);

    const a252MeshIdx = findColumnIndex(["a252 mesh"]);

    const b503MeshIdx = findColumnIndex(["b503 mesh"]);

    const b785MeshIdx = findColumnIndex(["b785 mesh"]);

    const concreteToFoundationIdx = findColumnIndex(["concrete to foundation"]);

    const asDugBackfillIdx = findColumnIndex(["as dug backfill"]);

    const stockpileOnsiteIdx = findColumnIndex([
      "stockpile on-site",
      "stockpile onsite",
    ]);

    const perforated150mmPipeIdx = findColumnIndex([
      "perforated 150mm pipe",
      "150mm pipe",
    ]);

    const concreteHaunchingToFrontIdx = findColumnIndex([
      "concrete haunching to front",
      "haunching to front",
    ]);

    const concreteHaunchingToRearIdx = findColumnIndex([
      "concrete haunching to rear",
      "haunching to rear",
    ]);

    const granularBackfillIdx = findColumnIndex(["granular backfill"]);

    const granularBackfill2Idx = findColumnIndex(["granular backfill"]);

    const terramIdx = findColumnIndex(["terram"]);

    const fencePostAt2400mmCcsIdx = findColumnIndex([
      "fence post at 2400mm ccs",
      "fence post 2400mm",
      "fence post",
    ]);

    const purchaseInclCarriageWasteFpIdx = findColumnIndex([
      "purchase incl carriage waste fp",
      "purchase incl carriage",
      "carriage waste",
    ]);

    const installGeoWallsIdx = findColumnIndex(["install geo walls"]);

    // TOBERMORE WALLS section - comprehensive search terms
    const excavateInCuttingsFpIdx = findColumnIndex([
      "excavate in cuttings fp",
      "excavate in cuttings",
      "cuttings fp",
    ]);

    const installGeoWallsTobermoreIdx = findColumnIndex(["install geo walls"]);

    const excavationOfEmbankmentIdx = findColumnIndex([
      "excavation of embankment",
      "embankment",
    ]);

    // Stockpile Onsite m2 -> Column Y (index 24)
    const stockpileOnsiteTobermoreIdx = 24; // Column Y

    const batterToFaceOfEmbankmentIdx = findColumnIndex([
      "batter to face of embankment",
      "batter to face",
    ]);

    const excavationOfBaseIdx = findColumnIndex(["excavation of base"]);

    // Stockpile Onsite m3 -> Column AB (index 27)
    const stockpileOnsite2Idx = 27; // Column AB

    // Level & compact formation m2 -> Column AC (index 28)
    const levelAndCompactFormationIdx = 28; // Column AC

    const concreteToBaseIdx = findColumnIndex(["concrete to base"]);

    const tobermoreSupplyOfGrandSecuraInclFpIdx = findColumnIndex([
      "tobermore supply of grand secura incl fp",
      "tobermore supply",
      "grand secura",
    ]);

    const tobermoreSecuraInstallationByMjIdx = findColumnIndex([
      "tobermore secura installation by mj",
      "secura installation",
      "installation by mj",
    ]);

    const tobermoreSupplyOfGeogridIdx = findColumnIndex([
      "tobermore supply of geogrid",
      "supply of geogrid",
    ]);

    const mjInstallationOfGeogridIdx = findColumnIndex([
      "mj installation of geogrid",
      "installation of geogrid",
    ]);

    // Fence post at 2400mm ccs m3 -> Column AI (index 34)
    const fencePostAt2400mmCcsTobermoreIdx = 34; // Column AI

    const freeDrainingGranularBackfill300wColumnIdx = findColumnIndex([
      "free draining granular backfill 300w column",
      "free draining granular",
      "300w column",
    ]);

    const granularBackfillToRearOfWallIdx = findColumnIndex([
      "granular backfill to rear of wall",
      "backfill to rear",
    ]);

    const landDrainToRearOfWallIdx = findColumnIndex([
      "land drain to rear of wall",
      "land drain",
    ]);

    const allowSiltTrapIdx = findColumnIndex(["allow silt trap", "silt trap"]);

    const allowToConnectOfLandDrainToPlotDrainageIdx = findColumnIndex([
      "allow to connect of land drain to plot drainage",
      "connect land drain",
      "land drain to plot drainage",
    ]);

    // Validation for essential columns
    if (plotNoIdx === -1) {
      console.error(
        "Could not find 'Plot No' column in the Excel file. Please check the header format.",
      );
      return [];
    }
    // Helper function to safely extract column values
    const getColumnValue = (
      row: any[],
      colIdx: number,
      defaultValue: any = 0,
    ) => {
      return colIdx !== -1 && row[colIdx] !== undefined && row[colIdx] !== null
        ? row[colIdx]
        : defaultValue;
    };

    const allImportedRows = dataRows
      .filter(
        (row) =>
          row &&
          row.length > 0 &&
          row.some(
            (cell) => cell !== null && cell !== undefined && cell !== "",
          ),
      )
      .map((row, index) => {
        return {
          key: `imported_${Date.now()}_${index}`,
          plotNo: String(getColumnValue(row, plotNoIdx, "")),
          housetype: String(getColumnValue(row, housetypeIdx, "")),
          // GEOWALLS
          excavateTrenchAndTipOnSite: Number(
            getColumnValue(row, excavateTrenchAndTipOnSiteIdx, 0),
          ),
          compactFormation: Number(getColumnValue(row, compactFormationIdx, 0)),
          concreteBlinding: Number(getColumnValue(row, concreteBlindingIdx, 0)),
          a393Mesh: Number(getColumnValue(row, a393MeshIdx, 0)),
          a252Mesh: Number(getColumnValue(row, a252MeshIdx, 0)),
          b503Mesh: Number(getColumnValue(row, b503MeshIdx, 0)),
          b785Mesh: Number(getColumnValue(row, b785MeshIdx, 0)),
          concreteToFoundation: Number(
            getColumnValue(row, concreteToFoundationIdx, 0),
          ),
          asDugBackfill: Number(getColumnValue(row, asDugBackfillIdx, 0)),
          stockpileOnsite: Number(getColumnValue(row, stockpileOnsiteIdx, 0)),
          perforated150mmPipe: Number(
            getColumnValue(row, perforated150mmPipeIdx, 0),
          ),
          concreteHaunchingToFront: Number(
            getColumnValue(row, concreteHaunchingToFrontIdx, 0),
          ),
          concreteHaunchingToRear: Number(
            getColumnValue(row, concreteHaunchingToRearIdx, 0),
          ),
          granularBackfill: Number(getColumnValue(row, granularBackfillIdx, 0)),
          granularBackfill2: Number(
            getColumnValue(row, granularBackfill2Idx, 0),
          ),
          terram: Number(getColumnValue(row, terramIdx, 0)),
          fencePostAt2400mmCcs: Number(
            getColumnValue(row, fencePostAt2400mmCcsIdx, 0),
          ),
          purchaseInclCarriageWasteFp: Number(
            getColumnValue(row, purchaseInclCarriageWasteFpIdx, 0),
          ),
          installGeoWalls: Number(getColumnValue(row, installGeoWallsIdx, 0)),
          // TOBERMORE WALLS
          excavateInCuttingsFp: Number(
            getColumnValue(row, excavateInCuttingsFpIdx, 0),
          ),
          installGeoWallsTobermore: Number(
            getColumnValue(row, installGeoWallsTobermoreIdx, 0),
          ),
          excavationOfEmbankment: Number(
            getColumnValue(row, excavationOfEmbankmentIdx, 0),
          ),
          stockpileOnsiteTobermore: Number(
            getColumnValue(row, stockpileOnsiteTobermoreIdx, 0),
          ),
          batterToFaceOfEmbankment: Number(
            getColumnValue(row, batterToFaceOfEmbankmentIdx, 0),
          ),
          excavationOfBase: Number(getColumnValue(row, excavationOfBaseIdx, 0)),
          stockpileOnsite2: Number(getColumnValue(row, stockpileOnsite2Idx, 0)),
          levelAndCompactFormation: Number(
            getColumnValue(row, levelAndCompactFormationIdx, 0),
          ),
          concreteToBase: Number(getColumnValue(row, concreteToBaseIdx, 0)),
          tobermoreSupplyOfGrandSecuraInclFp: Number(
            getColumnValue(row, tobermoreSupplyOfGrandSecuraInclFpIdx, 0),
          ),
          tobermoreSecuraInstallationByMj: Number(
            getColumnValue(row, tobermoreSecuraInstallationByMjIdx, 0),
          ),
          tobermoreSupplyOfGeogrid: Number(
            getColumnValue(row, tobermoreSupplyOfGeogridIdx, 0),
          ),
          mjInstallationOfGeogrid: Number(
            getColumnValue(row, mjInstallationOfGeogridIdx, 0),
          ),
          fencePostAt2400mmCcsTobermore: Number(
            getColumnValue(row, fencePostAt2400mmCcsTobermoreIdx, 0),
          ),
          freeDrainingGranularBackfill300wColumn: Number(
            getColumnValue(row, freeDrainingGranularBackfill300wColumnIdx, 0),
          ),
          granularBackfillToRearOfWall: Number(
            getColumnValue(row, granularBackfillToRearOfWallIdx, 0),
          ),
          landDrainToRearOfWall: Number(
            getColumnValue(row, landDrainToRearOfWallIdx, 0),
          ),
          allowSiltTrap: Number(getColumnValue(row, allowSiltTrapIdx, 0)),
          allowToConnectOfLandDrainToPlotDrainage: Number(
            getColumnValue(row, allowToConnectOfLandDrainToPlotDrainageIdx, 0),
          ),
        };
      })
      .filter((row) => {
        const plotNoStr = String(row.plotNo || "").trim();
        const hasValidPlotNo =
          plotNoStr !== "" &&
          plotNoStr !== "0" &&
          plotNoStr !== "null" &&
          plotNoStr !== "undefined";
        return hasValidPlotNo;
      });
    if (allImportedRows.length === 0) {
      console.error("No valid data found to import");
      return [];
    }

    // Count found columns for summary
    const foundColumns = [
      plotNoIdx,
      housetypeIdx,
      excavateTrenchAndTipOnSiteIdx,
      compactFormationIdx,
      concreteBlindingIdx,
      a393MeshIdx,
      a252MeshIdx,
      b503MeshIdx,
      b785MeshIdx,
      concreteToFoundationIdx,
      asDugBackfillIdx,
      stockpileOnsiteIdx,
      perforated150mmPipeIdx,
      concreteHaunchingToFrontIdx,
      concreteHaunchingToRearIdx,
      granularBackfillIdx,
      granularBackfill2Idx,
      terramIdx,
      fencePostAt2400mmCcsIdx,
      purchaseInclCarriageWasteFpIdx,
      installGeoWallsIdx,
      excavateInCuttingsFpIdx,
      installGeoWallsTobermoreIdx,
      excavationOfEmbankmentIdx,
      stockpileOnsiteTobermoreIdx,
      batterToFaceOfEmbankmentIdx,
      excavationOfBaseIdx,
      stockpileOnsite2Idx,
      levelAndCompactFormationIdx,
      concreteToBaseIdx,
      tobermoreSupplyOfGrandSecuraInclFpIdx,
      tobermoreSecuraInstallationByMjIdx,
      tobermoreSupplyOfGeogridIdx,
      mjInstallationOfGeogridIdx,
      fencePostAt2400mmCcsTobermoreIdx,
      freeDrainingGranularBackfill300wColumnIdx,
      granularBackfillToRearOfWallIdx,
      landDrainToRearOfWallIdx,
      allowSiltTrapIdx,
      allowToConnectOfLandDrainToPlotDrainageIdx,
    ].filter((idx) => idx !== -1).length;
    return allImportedRows;
  };

  // Helper function to process Plot Pavings data - exact logic from PlotPavings.tsx
  const processPlotPavingsData = (jsonData: any[][]) => {
    // Define excel columns for Plot Pavings (same as in PlotPavings.tsx)
    const excelColumns = [
      { key: "plotNo", title: "Plot No" },
      { key: "housetype", title: "House type" },
      // Paving Paths
      { key: "pavingLevelCompact", title: "Level & Compact (m²)" },
      { key: "pavingGeotextileLayer", title: "Geotextile Layer (m²)" },
      { key: "pavingSubBase", title: "xxxmm Sub base (m²)" },
      { key: "pavingPlainGreyPavings", title: "Plain Grey slabs (m²)" },
      { key: "slab900wSteps", title: "900w Steps (Nr)" },
      { key: "slab1200wSteps", title: "1200w Steps (Nr)" },
      { key: "slabGravelMarginPea", title: "Gravel Margin in pea gravel (Nr)" },
      {
        key: "slabGravelMarginCotswold",
        title: "Gravel Margin in Cotswold gravel (Nr)",
      },
      { key: "slabThresholdDrain", title: "Threshold Drain (Nr)" },
      { key: "slabShedBase", title: "Shed Base (Nr)" },
      { key: "slabCycleHoops", title: "Cycle Hoops (Nr)" },
      { key: "slabRotaryDryer", title: "Rotary Dryer (Nr)" },
      // Macadam Paths
      { key: "macadamLevelCompact", title: "Level & Compact (m²)" },
      { key: "macadamGeotextileLayer", title: "Geotextile Layer (m²)" },
      { key: "macadamSubBase", title: "xxxmm Sub base (m²)" },
      { key: "macadamDbBase", title: "xxmm DB Base (m²)" },
      { key: "macadamDbBinder", title: "xxmm DB Binder (m²)" },
      { key: "macadamSealingGrit", title: "Sealing Grit (m²)" },
      { key: "macadamSweepTack", title: "Sweep & Tack (m²)" },
      { key: "macadamDbSurfaceCourse", title: "xxmm DB Surface course (m²)" },
      { key: "macadamHraSurfaceCourse", title: "xxmm HRA Surface course (m²)" },
      { key: "macadamSmaSurfaceCourse", title: "xxmm SMA Surface course (m²)" },
      { key: "macadamEdgings", title: "Edgings (m)" },
    ];
    let headerRowIndex = -1;
    for (let i = 0; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (
        row.some(
          (cell) =>
            String(cell).includes("Plot") && String(cell).includes("No"),
        )
      ) {
        headerRowIndex = i;
        break;
      }
    }
    if (headerRowIndex === -1) {
      console.error("Could not find header row with 'Plot No'");
      return { data: [], columnHeaders: {}, dimensionValues: {} };
    }

    const headers = jsonData[headerRowIndex];
    // Skip columns C, D, E, F, G, H, I (indices 2-8)
    // Data starts at column J (index 9)
    const skipColumnsCount = 7; // Columns C through I
    const dataStartIndex = 2 + skipColumnsCount; // Start at index 9 (column J)
    // Extract column headers and dimension values
    const extractedHeaders: Record<string, string> = {};
    const extractedDimensionValues: Record<string, string> = {};

    // Build multi-row headers to search (up to 4 rows below headerRowIndex)
    const headerRowsToSearch: string[][] = [];
    for (let r = headerRowIndex; r <= Math.min(headerRowIndex + 4, jsonData.length - 1); r++) {
      if (jsonData[r]) {
        headerRowsToSearch.push(jsonData[r].map((cell: any) => String(cell || "").trim()));
      }
    }

    // Define fixed text for numeric dimension columns
    const dimensionFixedText: Record<string, string> = {
      pavingSubBase: "thick Stone bed",
      macadamSubBase: "thick MOT Type 1",
    };

    // Dropdown columns that match against evaluate codes — kept in lock-step
    // with PlotPavings.tsx's own Configure Headers modal <select> option lists
    // (macadamDbBinder previously listed ALP050/ALP053, which aren't options
    // in that select at all, and was missing its real ALP046/ALP047 options).
    const dropdownColumns: Record<string, string[]> = {
      pavingPlainGreyPavings: ["GWALP007", "GWALP008", "GWALP040", "GWALP041", "GWALP042", "GWALP043", "GWALP044", "GWALP045"],
      macadamDbBase: ["ALP041", "ALP042"],
      macadamDbBinder: ["ALP043", "ALP044", "ALP045", "ALP046", "ALP047"],
      macadamDbSurfaceCourse: ["ALP048", "ALP049", "ALP051"],
      macadamHraSurfaceCourse: ["ALP049", "ALP051"],
      macadamSmaSurfaceCourse: ["ALP052"],
    };

    // Only these 8 fields actually appear in PlotPavings.tsx's Configure
    // Headers modal (it's fully hardcoded there, not derived from excelColumns
    // like PlotDrives.tsx) — every other column ("Level & Compact",
    // "Geotextile Layer", the Slabs/Steps/Gravel Margin Nr-quantity fields,
    // "Sealing Grit", "Sweep & Tack", "Edgings", etc.) is fixed and has no
    // configuration to import at all, so its column title must never be
    // touched by import regardless of what text sits in that header cell.
    const CONFIGURE_HEADERS_ELIGIBLE_COLUMNS = new Set([
      "pavingSubBase", "pavingPlainGreyPavings",
      "macadamSubBase", "macadamDbBase", "macadamDbBinder",
      "macadamDbSurfaceCourse", "macadamHraSurfaceCourse", "macadamSmaSurfaceCourse",
    ]);

    // Case-insensitive exact match first, then substring match either
    // direction — same fallback order the equivalent Plot Drives import fix
    // uses, so a near-miss import still resolves to the option's exact label.
    const fuzzyMatchEvaluateCode = (cellValue: string, codes: string[]): string | undefined => {
      const cellLower = cellValue.toLowerCase();
      const withLabels = codes
        .map((code) => ({ code, label: EVALUATE_CODES[code as EvaluateCode]?.label }))
        .filter((c): c is { code: string; label: string } => !!c.label);
      return (
        withLabels.find((c) => cellLower === c.label.toLowerCase())?.code ||
        withLabels.find((c) => cellLower.includes(c.label.toLowerCase()) || c.label.toLowerCase().includes(cellLower))?.code
      );
    };

    // Map headers starting from column index 9 (after Plot No, House type, and skipped columns)
    excelColumns.forEach((col, index) => {
      if (index < 2) return; // Skip "plotNo" and "housetype"
      if (!CONFIGURE_HEADERS_ELIGIBLE_COLUMNS.has(col.key)) return; // Fixed, non-configurable field — never touched by import

      const headerIndex = dataStartIndex + (index - 2); // Offset by skipped columns
      if (headerIndex >= headers.length || !headers[headerIndex]) return;

      const headerValue = String(headers[headerIndex]).trim();
      extractedHeaders[col.key] = headerValue;

      // Check for dropdown columns first (Slabs, DB Base, DB Binder, Surface courses)
      if (dropdownColumns[col.key]) {
        // Search across multiple header rows for a matching evaluate code label
        for (const headerRow of headerRowsToSearch) {
          const cellValue = headerRow[headerIndex];
          if (!cellValue) continue;
          const matchedCode = fuzzyMatchEvaluateCode(cellValue, dropdownColumns[col.key]);
          if (matchedCode) {
            extractedDimensionValues[col.key] = matchedCode;
            extractedHeaders[col.key] = EVALUATE_CODES[matchedCode as EvaluateCode].label;
            break;
          }
        }
        // No option matched anything in the sheet — this isn't a real
        // configuration, so don't let the raw imported text through as the
        // column title either.
        if (!extractedDimensionValues[col.key]) {
          delete extractedHeaders[col.key];
        }
      }
      // Check if this column has dimension patterns in default title (for fields like pavingSubBase, macadamSubBase)
      else if (/\b(x{2,}(?:mm)?)\b/gi.test(col.title)) {
        const fixedText = dimensionFixedText[col.key];
        let found = false;

        // Search across multiple header rows
        for (const headerRow of headerRowsToSearch) {
          if (found) break;
          const cellValue = headerRow[headerIndex];
          if (!cellValue) continue;

          if (fixedText) {
            // Find the fixed text portion and extract the leading dimension before it
            const fixedTextIndex = cellValue.toLowerCase().indexOf(fixedText.toLowerCase());
            if (fixedTextIndex > 0) {
              const leadingText = cellValue.substring(0, fixedTextIndex).trim();
              const dimMatch = leadingText.match(/(\d+(?:\.\d+)?)\s*mm/i) || leadingText.match(/(\d+(?:\.\d+)?)\s*$/);
              if (dimMatch) {
                extractedDimensionValues[col.key] = dimMatch[1];
                found = true;
              }
            }
          }

          if (!found) {
            // Fallback: extract first number followed by mm
            const dimensionMatch = cellValue.match(/\b(\d+(?:\.\d+)?)\s*mm\b/i);
            if (dimensionMatch) {
              extractedDimensionValues[col.key] = dimensionMatch[1];
              found = true;
            }
          }
        }

        // No leading number found anywhere — a non-numeric header (e.g.
        // "xxxmm thick MOT Type 1" left as its own placeholder, or garbage
        // text) isn't a real thickness, so don't store it.
        if (!found) {
          delete extractedHeaders[col.key];
        }
      }
    });
    const allDataRows = jsonData.slice(headerRowIndex + 1);
    // Skip only the first row (totals row)
    const dataRows = allDataRows.slice(1);
    // Find column indices by matching header names
    const plotNoIdx = headers.findIndex(
      (h: any) => String(h).includes("Plot") && String(h).includes("No"),
    );
    const housetypeIdx = headers.findIndex(
      (h: any) =>
        String(h).toLowerCase().includes("house") &&
        String(h).toLowerCase().includes("type"),
    );
    const allImportedRows = dataRows
      .filter(
        (row) =>
          row &&
          row.length > 0 &&
          row.some(
            (cell) => cell !== null && cell !== undefined && cell !== "",
          ),
      )
      .map((row, index) => {
        return {
          key: `imported_${Date.now()}_${index}`,
          plotNo: String(row[plotNoIdx] || ""),
          housetype: String(row[housetypeIdx] || ""),
          // Data starts 9 columns after Plot No (skipping columns C-I which are indices 2-8)
          pavingLevelCompact:
            Math.round((Number(row[plotNoIdx + 9]) || 0) * 1000) / 1000,
          pavingGeotextileLayer:
            Math.round((Number(row[plotNoIdx + 10]) || 0) * 1000) / 1000,
          pavingSubBase:
            Math.round((Number(row[plotNoIdx + 11]) || 0) * 1000) / 1000,
          pavingPlainGreyPavings:
            Math.round((Number(row[plotNoIdx + 12]) || 0) * 1000) / 1000,
          slab900wSteps:
            Math.round((Number(row[plotNoIdx + 13]) || 0) * 1000) / 1000,
          slab1200wSteps:
            Math.round((Number(row[plotNoIdx + 14]) || 0) * 1000) / 1000,
          slabGravelMarginPea:
            Math.round((Number(row[plotNoIdx + 15]) || 0) * 1000) / 1000,
          slabGravelMarginCotswold:
            Math.round((Number(row[plotNoIdx + 16]) || 0) * 1000) / 1000,
          slabThresholdDrain:
            Math.round((Number(row[plotNoIdx + 17]) || 0) * 1000) / 1000,
          slabShedBase:
            Math.round((Number(row[plotNoIdx + 18]) || 0) * 1000) / 1000,
          slabCycleHoops:
            Math.round((Number(row[plotNoIdx + 19]) || 0) * 1000) / 1000,
          slabRotaryDryer:
            Math.round((Number(row[plotNoIdx + 20]) || 0) * 1000) / 1000,
          macadamLevelCompact:
            Math.round((Number(row[plotNoIdx + 21]) || 0) * 1000) / 1000,
          macadamGeotextileLayer:
            Math.round((Number(row[plotNoIdx + 22]) || 0) * 1000) / 1000,
          macadamSubBase:
            Math.round((Number(row[plotNoIdx + 23]) || 0) * 1000) / 1000,
          macadamDbBase:
            Math.round((Number(row[plotNoIdx + 24]) || 0) * 1000) / 1000,
          macadamDbBinder:
            Math.round((Number(row[plotNoIdx + 25]) || 0) * 1000) / 1000,
          macadamSealingGrit:
            Math.round((Number(row[plotNoIdx + 26]) || 0) * 1000) / 1000,
          macadamSweepTack:
            Math.round((Number(row[plotNoIdx + 27]) || 0) * 1000) / 1000,
          macadamDbSurfaceCourse:
            Math.round((Number(row[plotNoIdx + 28]) || 0) * 1000) / 1000,
          macadamHraSurfaceCourse:
            Math.round((Number(row[plotNoIdx + 29]) || 0) * 1000) / 1000,
          macadamSmaSurfaceCourse:
            Math.round((Number(row[plotNoIdx + 30]) || 0) * 1000) / 1000,
          macadamEdgings:
            Math.round((Number(row[plotNoIdx + 31]) || 0) * 1000) / 1000,
        };
      })
      .filter((row) => {
        const plotNoStr = String(row.plotNo || "").trim();
        const hasPlotNo =
          plotNoStr !== "" &&
          plotNoStr !== "0" &&
          plotNoStr !== "null" &&
          plotNoStr !== "undefined";
        const housetypeStr = String(row.housetype || "").trim();
        const hasValidHouseType =
          housetypeStr !== "" &&
          !housetypeStr.toLowerCase().includes("total") &&
          !housetypeStr.toLowerCase().includes("sum") &&
          isNaN(Number(housetypeStr)) && // Not purely numeric
          housetypeStr.match(/[a-zA-Z]/); // Contains letters
        return hasPlotNo && hasValidHouseType;
      });
    if (allImportedRows.length === 0) {
    }

    return {
      data: allImportedRows,
      columnHeaders: extractedHeaders,
      dimensionValues: extractedDimensionValues,
    };
  };

  // Helper function to process Scaffold data - exact logic from Scaffold.tsx
  const processScaffoldData = (jsonData: any[][]) => {
    // Define excel columns for Scaffold (same as in Scaffold.tsx)
    const excelColumns = [
      { key: "plotNo", title: "Plot No" },
      { key: "housetype", title: "House type" },
      { key: "scaffoldPlatform", title: "Scaffold Platform" },
      { key: "loadingBay4x2m", title: "Loading bay 4x2m" },
      { key: "stairAccess3x3m", title: "Stair Access 3x3m" },
      // Scaffold Platform
      { key: "scaffoldLevelCompact", title: "Level & Compact (m²)" },
      { key: "scaffoldGeotextileLayer", title: "Geotextile Layer (m²)" },
      { key: "scaffoldCrush6F2", title: "xxmm crush 6F2 (m²)" },
      { key: "scaffoldType1", title: "xxmm Type1 (m²)" },
      // Loading Bays
      { key: "loadingLevelCompact", title: "Level & Compact (m²)" },
      { key: "loadingGeotextileLayer", title: "Geotextile Layer (m²)" },
      { key: "loadingCrush6F2", title: "xxxmm crush 6F2 (m²)" },
      { key: "loadingType1", title: "xxmm Type1 (m²)" },
      // Stair Access
      { key: "stairLevelCompact", title: "Level & Compact (m²)" },
      { key: "stairGeotextileLayer", title: "Geotextile Layer (m²)" },
      { key: "stairCrush6F2", title: "xxxmm crush 6F2 (m²)" },
      { key: "stairType1", title: "xxmm Type1 (m²)" },
    ];
    let headerRowIndex = -1;
    for (let i = 0; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (
        row.some(
          (cell) =>
            String(cell).includes("Plot") && String(cell).includes("No"),
        )
      ) {
        headerRowIndex = i;
        break;
      }
    }
    if (headerRowIndex === -1) {
      console.error("Could not find header row with 'Plot No'");
      return { data: [], columnHeaders: {}, dimensionValues: {} };
    }

    const headers = jsonData[headerRowIndex];
    const allDataRows = jsonData.slice(headerRowIndex + 1);
    // Skip only the first row (totals row)
    const dataRows = allDataRows.slice(1);
    // Find column indices by matching header names
    const plotNoIdx = headers.findIndex(
      (h: any) => String(h).includes("Plot") && String(h).includes("No"),
    );
    const housetypeIdx = headers.findIndex(
      (h: any) =>
        String(h).toLowerCase().includes("house") &&
        String(h).toLowerCase().includes("type"),
    );

    // Base measurements
    const scaffoldPlatformIdx = headers.findIndex(
      (h: any) =>
        String(h).toLowerCase().includes("scaffold") &&
        String(h).toLowerCase().includes("platform"),
    );
    const loadingBay4x2mIdx = headers.findIndex(
      (h: any) =>
        String(h).toLowerCase().includes("loading") &&
        String(h).toLowerCase().includes("bay"),
    );
    const stairAccess3x3mIdx = headers.findIndex(
      (h: any) =>
        String(h).toLowerCase().includes("stair") &&
        String(h).toLowerCase().includes("access"),
    );

    // Build a column mapping by finding columns sequentially in the header
    const columnMapping: { [key: string]: number } = {};

    let currentSearchIndex = 0; // Track position to search from

    excelColumns.forEach((col, colArrayIndex) => {
      // For basic columns, use findIndex
      if (colArrayIndex < 5) {
        // Plot No, House type, Scaffold Platform, Loading bay, Stair Access
        if (col.key === "plotNo") {
          columnMapping[col.key] = plotNoIdx;
        } else if (col.key === "housetype") {
          columnMapping[col.key] = housetypeIdx;
        } else if (col.key === "scaffoldPlatform") {
          columnMapping[col.key] = scaffoldPlatformIdx;
        } else if (col.key === "loadingBay4x2m") {
          columnMapping[col.key] = loadingBay4x2mIdx;
        } else if (col.key === "stairAccess3x3m") {
          columnMapping[col.key] = stairAccess3x3mIdx;
        }
        currentSearchIndex = columnMapping[col.key] + 1;
      } else {
        // For other columns, search sequentially from currentSearchIndex
        // This ensures we find columns in order and don't match the wrong ones
        const foundIndex = headers.findIndex((h: any, idx: number) => {
          if (idx < currentSearchIndex) return false; // Only search forward

          const headerText = String(h || "")
            .toLowerCase()
            .trim();
          const titleText = col.title.toLowerCase();

          // Match based on the column title, but be flexible with dimensions
          // For dynamic columns (with xxmm, xxxmm), match the non-dimension part
          if (/(x{2,}mm?)/i.test(col.title)) {
            // This is a dynamic column - match the text pattern after the dimension
            // e.g., "xxxmm crush 6F2" should match "150mm crush 6F2"
            // Remove dimension pattern AND parentheses
            const patternWithoutDimension = titleText
              .replace(/(x{2,}mm?)/gi, "")
              .replace(/\(.*?\)/g, "")
              .trim();
            const headerWithoutParens = headerText
              .replace(/\(.*?\)/g, "")
              .trim();
            // Check if the header contains all words from the pattern
            const patternWords = patternWithoutDimension
              .split(/\s+/)
              .filter((w) => w.length > 0);
            const match = patternWords.every((word) =>
              headerWithoutParens.includes(word),
            );

            if (match) {
            }

            return match;
          } else {
            // Non-dynamic column - do exact matching
            const titleWords = titleText
              .replace(/\(.*?\)/g, "")
              .trim()
              .split(/\s+/);
            return titleWords.every((word) => headerText.includes(word));
          }
        });

        if (foundIndex >= 0) {
          columnMapping[col.key] = foundIndex;
          currentSearchIndex = foundIndex + 1;
        } else {
        }
      }
    });
    // Extract column headers and dimension values using the correct column mapping
    const extractedHeaders: Record<string, string> = {};
    const extractedDimensionValues: Record<string, string> = {};

    // Build multi-row headers to search (up to 4 rows below headerRowIndex)
    const headerRowsToSearch: string[][] = [];
    for (let r = headerRowIndex; r <= Math.min(headerRowIndex + 4, jsonData.length - 1); r++) {
      if (jsonData[r]) {
        headerRowsToSearch.push(jsonData[r].map((cell: any) => String(cell || "").trim()));
      }
    }

    // Define fixed text for each dynamic column to extract only the leading dimension
    const dimensionFixedText: Record<string, string> = {
      scaffoldCrush6F2: "crush 6F2",
      scaffoldType1: "Type1",
      loadingCrush6F2: "crush 6F2",
      loadingType1: "Type1",
      stairCrush6F2: "crush 6F2",
      stairType1: "Type1",
    };

    excelColumns.forEach((col) => {
      const actualColumnIndex = columnMapping[col.key];
      if (actualColumnIndex !== undefined && actualColumnIndex >= 0) {
        const headerValue = String(headers[actualColumnIndex] || "").trim();
        extractedHeaders[col.key] = headerValue;

        // Check if this column has dimension patterns in default title (xxmm, xxxmm, etc.)
        if (/(x{2,}mm?)/i.test(col.title)) {
          // Search across multiple header rows for the dimension value
          const fixedText = dimensionFixedText[col.key];
          let found = false;

          for (const headerRow of headerRowsToSearch) {
            if (found) break;
            const cellValue = headerRow[actualColumnIndex];
            if (!cellValue) continue;

            if (fixedText) {
              // Find the fixed text portion and extract the leading dimension before it
              const fixedTextIndex = cellValue.toLowerCase().indexOf(fixedText.toLowerCase());
              if (fixedTextIndex > 0) {
                const leadingText = cellValue.substring(0, fixedTextIndex).trim();
                // Extract numeric dimension (supports decimals like 12.34)
                const dimMatch = leadingText.match(/(\d+(?:\.\d+)?)\s*mm/i) || leadingText.match(/(\d+(?:\.\d+)?)\s*$/);
                if (dimMatch) {
                  extractedDimensionValues[col.key] = dimMatch[1];
                  found = true;
                }
              }
            }

            if (!found) {
              // Fallback: extract first number followed by mm
              const dimensionMatch = cellValue.match(/\b(\d+(?:\.\d+)?)\s*mm\b/i);
              if (dimensionMatch) {
                extractedDimensionValues[col.key] = dimensionMatch[1];
                found = true;
              }
            }
          }
        }
      }
    });

    // Extract Loading bay size from header (e.g., "Loading bay 4.5x2.5m" → W=4.5, H=2.5)
    const loadingBayHeader = extractedHeaders["loadingBay4x2m"] || "";
    const loadingBaySizeMatch = loadingBayHeader.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*m/i);
    if (loadingBaySizeMatch) {
      extractedDimensionValues["loadingBaySizeW"] = loadingBaySizeMatch[1];
      extractedDimensionValues["loadingBaySizeH"] = loadingBaySizeMatch[2];
      extractedDimensionValues["loadingBaySize"] = `${loadingBaySizeMatch[1]}x${loadingBaySizeMatch[2]}m`;
    } else {
      // Search multi-row headers for loading bay size
      for (const headerRow of headerRowsToSearch) {
        const cellValue = headerRow[columnMapping["loadingBay4x2m"]];
        if (!cellValue) continue;
        const match = cellValue.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*m/i);
        if (match) {
          extractedDimensionValues["loadingBaySizeW"] = match[1];
          extractedDimensionValues["loadingBaySizeH"] = match[2];
          extractedDimensionValues["loadingBaySize"] = `${match[1]}x${match[2]}m`;
          break;
        }
      }
    }

    // Extract Stair Access size from header (e.g., "Stair Access 3.5x3.5m" → W=3.5, H=3.5)
    const stairAccessHeader = extractedHeaders["stairAccess3x3m"] || "";
    const stairAccessSizeMatch = stairAccessHeader.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*m/i);
    if (stairAccessSizeMatch) {
      extractedDimensionValues["stairAccessSizeW"] = stairAccessSizeMatch[1];
      extractedDimensionValues["stairAccessSizeH"] = stairAccessSizeMatch[2];
      extractedDimensionValues["stairAccessSize"] = `${stairAccessSizeMatch[1]}x${stairAccessSizeMatch[2]}m`;
    } else {
      // Search multi-row headers for stair access size
      for (const headerRow of headerRowsToSearch) {
        const cellValue = headerRow[columnMapping["stairAccess3x3m"]];
        if (!cellValue) continue;
        const match = cellValue.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*m/i);
        if (match) {
          extractedDimensionValues["stairAccessSizeW"] = match[1];
          extractedDimensionValues["stairAccessSizeH"] = match[2];
          extractedDimensionValues["stairAccessSize"] = `${match[1]}x${match[2]}m`;
          break;
        }
      }
    }
    // Scaffold Platform section
    const scaffoldLevelCompactIdx = columnMapping["scaffoldLevelCompact"] ?? -1;
    const scaffoldGeotextileLayerIdx =
      columnMapping["scaffoldGeotextileLayer"] ?? -1;
    const scaffoldCrush6F2Idx = columnMapping["scaffoldCrush6F2"] ?? -1;
    const scaffoldType1Idx = columnMapping["scaffoldType1"] ?? -1;

    // Loading Bays section
    const loadingLevelCompactIdx = columnMapping["loadingLevelCompact"] ?? -1;
    const loadingGeotextileLayerIdx =
      columnMapping["loadingGeotextileLayer"] ?? -1;
    const loadingCrush6F2Idx = columnMapping["loadingCrush6F2"] ?? -1;
    const loadingType1Idx = columnMapping["loadingType1"] ?? -1;
    // Stair Access section
    const stairLevelCompactIdx = columnMapping["stairLevelCompact"] ?? -1;
    const stairGeotextileLayerIdx = columnMapping["stairGeotextileLayer"] ?? -1;
    const stairCrush6F2Idx = columnMapping["stairCrush6F2"] ?? -1;
    const stairType1Idx = columnMapping["stairType1"] ?? -1;
    // Detect "GARAGES" separator row to tag rows as plot or garage
    let isGarageSection = false;
    const allImportedRows: any[] = [];
    const nonEmptyRows = dataRows.filter(
      (row) =>
        row &&
        row.length > 0 &&
        row.some(
          (cell) => cell !== null && cell !== undefined && cell !== "",
        ),
    );
    nonEmptyRows.forEach((row, index) => {
      const firstCell = String(row[plotNoIdx] || "").trim().toUpperCase();
      if (firstCell === "GARAGES" || firstCell === "GARAGE") {
        isGarageSection = true;
        return; // Skip the separator row
      }
      const plotNoStr = String(row[plotNoIdx] || "").trim();
      const hasPlotNo =
        plotNoStr !== "" &&
        plotNoStr !== "0" &&
        plotNoStr !== "null" &&
        plotNoStr !== "undefined";
      const housetypeStr = String(row[housetypeIdx] || "").trim();
      const hasValidHouseType =
        housetypeStr !== "" &&
        !housetypeStr.toLowerCase().includes("total") &&
        !housetypeStr.toLowerCase().includes("sum") &&
        isNaN(Number(housetypeStr)) && // Not purely numeric
        housetypeStr.match(/[a-zA-Z]/); // Contains letters
      if (!hasPlotNo || !hasValidHouseType) return;

      allImportedRows.push({
        key: `imported_${Date.now()}_${index}`,
        plotNo: String(row[plotNoIdx] || ""),
        housetype: String(row[housetypeIdx] || ""),
        plotType: isGarageSection ? "garage" : "plot",
        scaffoldPlatform:
          Math.round(Number(row[scaffoldPlatformIdx] || 0) * 1000) / 1000,
        loadingBay4x2m:
          Math.round(Number(row[loadingBay4x2mIdx] || 0) * 1000) / 1000,
        stairAccess3x3m:
          Math.round(Number(row[stairAccess3x3mIdx] || 0) * 1000) / 1000,
        scaffoldLevelCompact:
          Math.round(Number(row[scaffoldLevelCompactIdx] || 0) * 1000) / 1000,
        scaffoldGeotextileLayer:
          Math.round(Number(row[scaffoldGeotextileLayerIdx] || 0) * 1000) /
          1000,
        scaffoldCrush6F2:
          Math.round(Number(row[scaffoldCrush6F2Idx] || 0) * 1000) / 1000,
        scaffoldType1:
          Math.round(Number(row[scaffoldType1Idx] || 0) * 1000) / 1000,
        loadingLevelCompact:
          Math.round(Number(row[loadingLevelCompactIdx] || 0) * 1000) / 1000,
        loadingGeotextileLayer:
          Math.round(Number(row[loadingGeotextileLayerIdx] || 0) * 1000) /
          1000,
        loadingCrush6F2:
          Math.round(Number(row[loadingCrush6F2Idx] || 0) * 1000) / 1000,
        loadingType1:
          Math.round(Number(row[loadingType1Idx] || 0) * 1000) / 1000,
        stairLevelCompact:
          Math.round(Number(row[stairLevelCompactIdx] || 0) * 1000) / 1000,
        stairGeotextileLayer:
          Math.round(Number(row[stairGeotextileLayerIdx] || 0) * 1000) / 1000,
        stairCrush6F2:
          Math.round(Number(row[stairCrush6F2Idx] || 0) * 1000) / 1000,
        stairType1: Math.round(Number(row[stairType1Idx] || 0) * 1000) / 1000,
      });
    });
    if (allImportedRows.length === 0) {
    }

    return {
      data: allImportedRows,
      columnHeaders: extractedHeaders,
      dimensionValues: extractedDimensionValues,
    };
  };

  // Helper function to process Drainage data (Simplified for Standard View)
  const processDrainageData = (jsonData: any[][]) => {
    // Header row ("Plot No"/"House type") found dynamically rather than by a
    // hardcoded absolute index (jsonData[3], still correct for THIS sheet's
    // current layout — verified against `sheet_to_json(..., blankrows: false)`,
    // which drops this sheet's one truly-blank row so its 2 category/sub-
    // category rows above the header land at indices 1-2) — the identical
    // "assume index 3" hardcode in the sibling processPlotDrivesData below was
    // wrong for its own sheet (only 1 category row above the header there, not
    // 2), which is what actually broke Plot Drives import. Making this one
    // dynamic too, rather than leaving it as a second hardcoded number that
    // happens to still be right, avoids the same class of bug recurring here
    // the next time this sheet's row count changes.
    // The single combined data row (this sheet has no per-plot rows — the
    // "Specified Supplier" row is a whole-tender total) sits exactly 3 rows
    // below the header row (header, units, a totals/"Man Entry" label row,
    // then data) — same relative-offset convention this workbook uses
    // elsewhere (see processGeoToberWallFromRetScreenData's TPCW column row).
    let headerRowIndex = -1;
    for (let i = 0; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (row && row.some((cell) => String(cell).trim() === "Plot No")) {
        headerRowIndex = i;
        break;
      }
    }
    if (headerRowIndex === -1) {
      console.error("[DrainageImport] Could not find header row with 'Plot No' — sheet layout may have changed.");
    }
    const headerRow = headerRowIndex !== -1 ? jsonData[headerRowIndex] || [] : [];
    const dataRows = headerRowIndex !== -1 ? jsonData.slice(headerRowIndex + 3, headerRowIndex + 4) : [];

    // Helper to find all occurrences of a column header
    const findAllColumns = (searchTerm: string): number[] => {
      const searchLower = searchTerm.toLowerCase().trim();
      const results: number[] = [];
      for (let i = 0; i < headerRow.length; i++) {
        const header = String(headerRow[i] || "")
          .toLowerCase()
          .trim();
        if (header.includes(searchLower)) {
          results.push(i);
        }
      }
      return results;
    };

    // Find base columns
    const plotNoIdx = 0;
    const housetypeIdx = 1;

    // Find common column types (Standard View)
    const labPerPlotCols = findAllColumns("lab per plot");
    const ppicCols = findAllColumns("ppic").filter((col, idx, arr) => {
      const header = String(headerRow[col] || "").toLowerCase();
      return !header.includes("mini") && !header.includes("nme");
    });
    const miniPpicCols = findAllColumns("mini ppic");
    const svpCols = findAllColumns("svp");
    // Try multiple variations to find "Concrete to rest bends"
    let concreteRestCols = findAllColumns("concrete to rest bends");
    if (concreteRestCols.length === 0) {
      concreteRestCols = findAllColumns("concrete to rest bend");
    }
    if (concreteRestCols.length === 0) {
      concreteRestCols = findAllColumns("concrete to rest");
    }
    if (concreteRestCols.length === 0) {
      concreteRestCols = findAllColumns("concrtete to rest"); // Handle misspelling
    }
    if (concreteRestCols.length === 0) {
      concreteRestCols = findAllColumns("concrete rest");
    }
    const junction100Cols = findAllColumns("100/100 junction").filter(c => {
      const header = String(headerRow[c] || "").toLowerCase();
      return !header.includes("triple socket") && !header.includes("triple)");
    });
    const pipe100Cols = findAllColumns("100mm pipe");
    const coupler100Cols = findAllColumns("100mm coupler");
    const junction100_150Cols = findAllColumns("100/150");
    const junction150_150Cols = findAllColumns("150/150");
    const pipe150Cols = findAllColumns("150mm dia pipe");
    const coupler150Cols = findAllColumns("150mm coupler");
    const gravelCols = findAllColumns("gravel");
    const concreteBedCols = findAllColumns("concrete bed");
    const tipOnsiteCols = findAllColumns("tip onsite");
    const roddingEyeCols = findAllColumns("rodding eye");
    const rwpCols = findAllColumns("rwp");
    const tripleSocketCols = findAllColumns("triple socket");
    const nmePpicCols = findAllColumns("nme ppic").filter((col) => {
      const header = String(headerRow[col] || "").toLowerCase();
      return !header.includes("600mm");
    });
    const nmePpic600Cols = findAllColumns("600mm nme ppic");
    // Filter to supplier sections only (col > 12) to avoid matching manual entry cols at 0-12
    const yardGullyCols = findAllColumns("yard gully").filter((c) => c > 12);
    // Find drainage channel columns - search for all possible header variations
    let drainageChannelCols = findAllColumns("drainage channel").filter(
      (c) => c > 12,
    );
    // Find land drain columns - search for all possible header variations and combine
    const landDrainSearchTerms = ["land drain", "land drainage", "silt trap", "connection to drainage"];
    let landDrainCols: number[] = [];
    for (const term of landDrainSearchTerms) {
      const found = findAllColumns(term).filter((c) => c > 12 && !landDrainCols.includes(c));
      landDrainCols.push(...found);
    }
    landDrainCols.sort((a, b) => a - b);

    // Extract dynamic column headers for Drainage Channel and Land Drain
    const extractedColumnHeaders: Record<string, string> = {};
    const extractedDimensionValues: Record<string, string> = {};

    // Helper to find first non-empty numeric value scanning right from colIdx
    const findNumericValue = (row: any[], colIdx: number): string | null => {
      for (const offset of [1, 2, 3]) {
        const val = row[colIdx + offset];
        if (val !== undefined && val !== null && val !== "" && !isNaN(Number(val))) {
          return String(val);
        }
      }
      return null;
    };

    // Search all rows for "Nr Plots Take off" and "Total Plots" labels.
    // Track current supplier section by detecting "osma" or "polypipe" header rows.
    let currentSupplierSection: "osma" | "polypipe" | null = null;
    for (let rowIdx = 0; rowIdx < jsonData.length; rowIdx++) {
      const row = jsonData[rowIdx] || [];
      for (let colIdx = 0; colIdx < row.length; colIdx++) {
        const cell = String(row[colIdx] || "").toLowerCase().trim();
        if (cell === "osma" || cell.includes("osma foul") || cell.includes("foul osma") || cell.includes("osma take off")) {
          currentSupplierSection = "osma";
          console.log(`[DrainageImport] Section detected: OSMA at row=${rowIdx} col=${colIdx} cell="${cell}"`);
        } else if (cell === "polypipe" || cell.includes("polypipe foul") || cell.includes("foul polypipe") || cell.includes("polypipe take off")) {
          currentSupplierSection = "polypipe";
          console.log(`[DrainageImport] Section detected: Polypipe at row=${rowIdx} col=${colIdx} cell="${cell}"`);
        }
        if (
          cell.includes("nr plots take off") || cell.includes("nr. plots take off") || cell === "nr plots takeoff"
        ) {
          const val = findNumericValue(row, colIdx);
          console.log(`[DrainageImport] "Nr Plots Take off" found at row=${rowIdx} col=${colIdx}, section=${currentSupplierSection}, value=${val}, row=`, row);
          if (val !== null) {
            if (currentSupplierSection === "polypipe") {
              extractedDimensionValues["polypipeNrPlotsTakeOff"] = val;
            } else {
              // Default to OSMA (first section or unlabelled)
              extractedDimensionValues["foulNrPlotsTakeOff"] = val;
            }
          }
        }
        if (
          cell.includes("total plots") &&
          !cell.includes("subtotal")
        ) {
          const val = findNumericValue(row, colIdx);
          console.log(`[DrainageImport] "Total Plots" found at row=${rowIdx} col=${colIdx}, section=${currentSupplierSection}, value=${val}, row=`, row);
          if (val !== null) {
            if (currentSupplierSection === "polypipe") {
              extractedDimensionValues["polypipeTotalPlots"] = val;
            } else {
              extractedDimensionValues["foulTotalPlots"] = val;
            }
          }
        }
      }
    }
    console.log("[DrainageImport] Final extractedDimensionValues:", extractedDimensionValues);

    // Drainage Channel options: GWALPDD020 = "Drainage channel", GWALPDD059 = "A15 Drainage channel", GWALPDD060 = "B125 Drainage channel"
    const drainageChannelOptions: { code: string; label: string }[] = [
      { code: "GWALPDD059", label: "A15 Drainage channel" },
      { code: "GWALPDD060", label: "B125 Drainage channel" },
      { code: "GWALPDD020", label: "Drainage channel" },
    ];
    // Land Drain options: GWALPDD056 = "Land drainage", GWALPDD057 = "Connection to drainage system", L&P3 = "Silt trap chamber"
    const landDrainOptions: { code: string; label: string }[] = [
      { code: "GWALPDD057", label: "Connection to drainage system" },
      { code: "L&P3", label: "Silt trap chamber" },
      { code: "GWALPDD056", label: "Land drainage" },
    ];

    // Match OSMA STORM Drainage Channel (first occurrence)
    if (drainageChannelCols.length > 0) {
      const headerValue = String(headerRow[drainageChannelCols[0]] || "").trim();
      for (const opt of drainageChannelOptions) {
        if (headerValue.toLowerCase().includes(opt.label.toLowerCase())) {
          extractedColumnHeaders["osmaStormDrainageChannel"] = opt.label;
          extractedDimensionValues["osmaStormDrainageChannel"] = opt.code;
          break;
        }
      }
    }
    // Match OSMA STORM Land Drain (first occurrence)
    if (landDrainCols.length > 0) {
      const headerValue = String(headerRow[landDrainCols[0]] || "").trim();
      for (const opt of landDrainOptions) {
        if (headerValue.toLowerCase().includes(opt.label.toLowerCase())) {
          extractedColumnHeaders["osmaStormLandDrain"] = opt.label;
          extractedDimensionValues["osmaStormLandDrain"] = opt.code;
          break;
        }
      }
    }
    // Match POLYPIPE STORM Drainage Channel (second occurrence)
    if (drainageChannelCols.length > 1) {
      const headerValue = String(headerRow[drainageChannelCols[1]] || "").trim();
      for (const opt of drainageChannelOptions) {
        if (headerValue.toLowerCase().includes(opt.label.toLowerCase())) {
          extractedColumnHeaders["polypipeStormDrainageChannel"] = opt.label;
          extractedDimensionValues["polypipeStormDrainageChannel"] = opt.code;
          break;
        }
      }
    }
    // Match POLYPIPE STORM Land Drain (second occurrence)
    if (landDrainCols.length > 1) {
      const headerValue = String(headerRow[landDrainCols[1]] || "").trim();
      for (const opt of landDrainOptions) {
        if (headerValue.toLowerCase().includes(opt.label.toLowerCase())) {
          extractedColumnHeaders["polypipeStormLandDrain"] = opt.label;
          extractedDimensionValues["polypipeStormLandDrain"] = opt.code;
          break;
        }
      }
    }

    // Create simplified row structure for Standard View (imports to drainageTableStandard)
    const allImportedRows = dataRows
      .filter((row) => row && row.length > 0)
      .map((row, index) => {
        const rowData: any = {
          key: `imported_${Date.now()}_${index}`,
          plotNo: String(row[plotNoIdx] || ""),
          housetype: String(row[housetypeIdx] || ""),
          plots: Math.round(Number(row[2] || 0) * 1000) / 1000,
          apartments: Math.round(Number(row[3] || 0) * 1000) / 1000,
          nmePhic: Math.round(Number(row[4] || 0) * 1000) / 1000,
          yardGully: Math.round(Number(row[5] || 0) * 1000) / 1000,
          drainageChannel: Math.round(Number(row[6] || 0) * 1000) / 1000,
          landDrain: Math.round(Number(row[7] || 0) * 1000) / 1000,
          cellularAtten: Math.round(Number(row[8] || 0) * 1000) / 1000,
          polypipeFoul: String(row[9] || ""),
          polypipeStorm: String(row[10] || ""),
          osmaFoul: String(row[11] || ""),
          osmaStorm: String(row[12] || ""),
          // OSMA FOUL section
          osma32mmBend:
            Math.round(Number(row[labPerPlotCols[0]] || 0) * 1000) / 1000,
          osmaPPIC: Math.round(Number(row[ppicCols[0]] || 0) * 1000) / 1000,
          osma50mmBend:
            Math.round(Number(row[miniPpicCols[0]] || 0) * 1000) / 1000,
          osma68mmBend: Math.round(Number(row[svpCols[0]] || 0) * 1000) / 1000,
          osma82mmBend:
            Math.round(Number(row[concreteRestCols[0]] || 0) * 1000) / 1000,
          osma110mmBend:
            Math.round(Number(row[junction100Cols[0]] || 0) * 1000) / 1000,
          osma32mmPipe:
            Math.round(Number(row[pipe100Cols[0]] || 0) * 1000) / 1000,
          osma40mmPipe:
            Math.round(Number(row[coupler100Cols[0]] || 0) * 1000) / 1000,
          osma50mmPipe:
            Math.round(Number(row[junction100_150Cols[0]] || 0) * 1000) / 1000,
          osma68mmPipe:
            Math.round(Number(row[junction150_150Cols[0]] || 0) * 1000) / 1000,
          osma82mmPipe:
            Math.round(Number(row[pipe150Cols[0]] || 0) * 1000) / 1000,
          osma110mmPipe:
            Math.round(Number(row[coupler150Cols[0]] || 0) * 1000) / 1000,
          gravel: Math.round(Number(row[gravelCols[0]] || 0) * 1000) / 1000,
          concealedSetSurround:
            Math.round(Number(row[concreteBedCols[0]] || 0) * 1000) / 1000,
          tipOnSite:
            Math.round(Number(row[tipOnsiteCols[0]] || 0) * 1000) / 1000,
          osmaNmePpic:
            Math.round(Number(row[nmePpicCols[0]] || 0) * 1000) / 1000,
          osma600mmNmePpic:
            Math.round(Number(row[nmePpic600Cols[0]] || 0) * 1000) / 1000,
          // OSMA STORM section
          osmaStorm32mmBend:
            Math.round(Number(row[labPerPlotCols[1]] || 0) * 1000) / 1000,
          osmaStorPPIC: Math.round(Number(row[ppicCols[1]] || 0) * 1000) / 1000,
          osmaStorm50mmBend:
            Math.round(Number(row[roddingEyeCols[0]] || 0) * 1000) / 1000,
          osmaStorm68mmBend:
            Math.round(Number(row[rwpCols[0]] || 0) * 1000) / 1000,
          osmaStorm100100JunctionTripleSocket:
            Math.round(Number(row[tripleSocketCols[0]] || 0) * 1000) / 1000,
          osmaStorm82mmBend:
            Math.round(Number(row[pipe100Cols[1]] || 0) * 1000) / 1000,
          osmaStorm110mmBend:
            Math.round(Number(row[coupler100Cols[1]] || 0) * 1000) / 1000,
          osmaStorm32mmPipe:
            Math.round(Number(row[junction100_150Cols[1]] || 0) * 1000) / 1000,
          osmaStorm40mmPipe:
            Math.round(Number(row[junction150_150Cols[1]] || 0) * 1000) / 1000,
          osmaStorm50mmPipe:
            Math.round(Number(row[pipe150Cols[1]] || 0) * 1000) / 1000,
          osmaStorm68mmPipe:
            Math.round(Number(row[coupler150Cols[1]] || 0) * 1000) / 1000,
          osmaStormGravel:
            Math.round(Number(row[gravelCols[1]] || 0) * 1000) / 1000,
          osmaStormConcreteBedSurround:
            Math.round(Number(row[concreteBedCols[1]] || 0) * 1000) / 1000,
          osmaStormTipOnsite:
            Math.round(Number(row[tipOnsiteCols[1]] || 0) * 1000) / 1000,
          osmaStormNmePpic:
            Math.round(Number(row[nmePpicCols[1]] || 0) * 1000) / 1000,
          osmaStorm600mmNmePpic:
            Math.round(Number(row[nmePpic600Cols[1]] || 0) * 1000) / 1000,
          osmaStormYardGully:
            Math.round(Number(row[yardGullyCols[0]] || 0) * 1000) / 1000,
          osmaStormDrainageChannel:
            Math.round(Number(row[drainageChannelCols[0]] || 0) * 1000) / 1000,
          osmaStormLandDrain:
            Math.round(Number(row[landDrainCols[0]] || 0) * 1000) / 1000,
          // POLYPIPE FOUL section
          polypipe32mmBend:
            Math.round(Number(row[labPerPlotCols[2]] || 0) * 1000) / 1000,
          polypipePPIC: Math.round(Number(row[ppicCols[2]] || 0) * 1000) / 1000,
          polypipe50mmBend:
            Math.round(Number(row[miniPpicCols[1]] || 0) * 1000) / 1000,
          polypipe68mmBend:
            Math.round(Number(row[svpCols[1]] || 0) * 1000) / 1000,
          polypipe82mmBend:
            Math.round(Number(row[concreteRestCols[1]] || 0) * 1000) / 1000, // Concrete to rest bends
          polypipe110mmBend:
            Math.round(Number(row[junction100Cols[1]] || 0) * 1000) / 1000,
          polypipe32mmPipe:
            Math.round(Number(row[pipe100Cols[2]] || 0) * 1000) / 1000,
          polypipe40mmPipe:
            Math.round(Number(row[coupler100Cols[2]] || 0) * 1000) / 1000,
          polypipe50mmPipe:
            Math.round(Number(row[junction100_150Cols[2]] || 0) * 1000) / 1000,
          polypipe68mmPipe:
            Math.round(Number(row[junction150_150Cols[2]] || 0) * 1000) / 1000,
          polypipe82mmPipe:
            Math.round(Number(row[pipe150Cols[2]] || 0) * 1000) / 1000,
          polypipe110mmPipe:
            Math.round(Number(row[coupler150Cols[2]] || 0) * 1000) / 1000,
          polypipeGravel:
            Math.round(Number(row[gravelCols[2]] || 0) * 1000) / 1000,
          polypipeConcreteBedSurround:
            Math.round(Number(row[concreteBedCols[2]] || 0) * 1000) / 1000,
          polypipeTipOnSite:
            Math.round(Number(row[tipOnsiteCols[2]] || 0) * 1000) / 1000,
          polypipeNmePpic:
            Math.round(Number(row[nmePpicCols[2]] || 0) * 1000) / 1000,
          polypipe600mmNmePpic:
            Math.round(Number(row[nmePpic600Cols[2]] || 0) * 1000) / 1000,
          // POLYPIPE STORM section
          polypipeStorm32mmBend:
            Math.round(Number(row[labPerPlotCols[3]] || 0) * 1000) / 1000,
          polypipeStormPPIC:
            Math.round(Number(row[ppicCols[3]] || 0) * 1000) / 1000,
          polypipeStorm50mmBend:
            Math.round(Number(row[roddingEyeCols[1]] || 0) * 1000) / 1000,
          polypipeStorm68mmBend:
            Math.round(Number(row[rwpCols[1]] || 0) * 1000) / 1000,
          polypipeStorm100100JunctionTripleSocket:
            Math.round(Number(row[tripleSocketCols[1]] || 0) * 1000) / 1000,
          polypipeStorm82mmBend:
            Math.round(Number(row[pipe100Cols[3]] || 0) * 1000) / 1000,
          polypipeStorm110mmBend:
            Math.round(Number(row[coupler100Cols[3]] || 0) * 1000) / 1000,
          polypipeStorm32mmPipe:
            Math.round(Number(row[junction100_150Cols[3]] || 0) * 1000) / 1000,
          polypipeStorm40mmPipe:
            Math.round(Number(row[junction150_150Cols[3]] || 0) * 1000) / 1000,
          polypipeStorm50mmPipe:
            Math.round(Number(row[pipe150Cols[3]] || 0) * 1000) / 1000,
          polypipeStorm68mmPipe:
            Math.round(Number(row[coupler150Cols[3]] || 0) * 1000) / 1000,
          polypipeStormGravel:
            Math.round(Number(row[gravelCols[3]] || 0) * 1000) / 1000,
          polypipeStormConcreteBedSurround:
            Math.round(Number(row[concreteBedCols[3]] || 0) * 1000) / 1000,
          polypipeStormTipOnSite:
            Math.round(Number(row[tipOnsiteCols[3]] || 0) * 1000) / 1000,
          polypipeStormNmePpic:
            Math.round(Number(row[nmePpicCols[3]] || 0) * 1000) / 1000,
          polypipeStorm600mmNmePpic:
            Math.round(Number(row[nmePpic600Cols[3]] || 0) * 1000) / 1000,
          polypipeStormYardGully:
            Math.round(Number(row[yardGullyCols[1]] || 0) * 1000) / 1000,
          polypipeStormDrainageChannel:
            Math.round(Number(row[drainageChannelCols[1]] || 0) * 1000) / 1000,
          polypipeStormLandDrain:
            Math.round(Number(row[landDrainCols[1]] || 0) * 1000) / 1000,
        };

        return rowData;
      });
    const importedRows = allImportedRows.filter((row) => {
      const plotNoStr = String(row.plotNo || "").trim();
      return plotNoStr !== "" && plotNoStr !== "0";
    });

    return { data: importedRows, columnHeaders: extractedColumnHeaders, dimensionValues: extractedDimensionValues };
  };

  // Helper function to process Plot Drives data (73 columns)
  const processPlotDrivesData = (jsonData: any[][]) => {
    // Row holding "Plot No"/"House type" — found dynamically rather than the
    // previous hardcoded `headerRowIndex = 3`, which read whatever row this
    // sheet's template revision happened to put there (currently "Private
    // Macadam Drive", a sub-category label one row below the real "Plot No"
    // row) instead of "Plot No" itself. Used below only to anchor the search
    // for the REAL per-column header row, not to read headers directly off —
    // see the next block.
    let headerRowIndex = -1;
    for (let i = 0; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (row && row.some((cell) => String(cell).trim() === "Plot No")) {
        headerRowIndex = i;
        break;
      }
    }
    if (headerRowIndex === -1) {
      console.error("[PlotDrivesImport] Could not find header row with 'Plot No' — sheet layout may have changed.");
      return { data: [], columnHeaders: {}, dimensionValues: {} };
    }

    // The row holding "Plot No"/"House type" is NOT the same row as the real
    // per-column headers ("Level and compact formation", "Geotextile layer",
    // ...) — this sheet stacks several increasingly-specific label rows below
    // it first (a top category like "DRIVES TO BINDER", then a section like
    // "PRIVATE DRIVES", then a sub-section like "Private Macadam Drive")
    // before the actual detailed header row. How many rows that takes has
    // already changed once when the template gained its GWKS_* key row, so
    // rather than hardcode yet another relative offset, scan a window below
    // "Plot No" and pick whichever row has the most populated text cells —
    // every row above the real header is a sparse label with only a handful
    // of cells filled, while the real header row lists 70+ distinct titles.
    let headers: any[] = jsonData[headerRowIndex] || [];
    let bestHeaderCellCount = headers.filter((c) => typeof c === "string" && c.trim()).length;
    for (let i = headerRowIndex + 1; i <= headerRowIndex + 5 && i < jsonData.length; i++) {
      const row = jsonData[i] || [];
      const count = row.filter((c) => typeof c === "string" && c.trim()).length;
      if (count > bestHeaderCellCount) {
        bestHeaderCellCount = count;
        headers = row;
      }
    }
    // Plot No and Housetype are BASE COLUMNS - always at index 0 and 1
    const plotNoIdx = 0;
    const housetypeIdx = 1;

    // Build column index map - maps col1, col2, ... col73 to Excel column indices
    const columnIndexMap: Record<string, number> = {};
    const extractedHeaders: Record<string, string> = {};
    let colNumber = 1;

    // Start from column 2 (after Plot No and Housetype)
    for (let i = 2; i < headers.length && colNumber <= 77; i++) {
      const headerValue = String(headers[i] || "").trim();
      if (headerValue) {
        const colKey = `col${colNumber}`;
        columnIndexMap[colKey] = i;
        extractedHeaders[colKey] = headerValue;
        colNumber++;
      }
    }
    // Fix section boundaries: each drive type's "Level & Compact" must land at its
    // expected col position [col1, col7, col14, col20, col26, col33]. When the Excel
    // is missing columns in a section, the next section's Level & Compact shifts left.
    // Detect and insert empty slots to realign all sections.
    const expectedSectionStarts = [1, 7, 14, 20, 26, 33];

    const actualSectionStarts = Object.keys(extractedHeaders)
      .filter((key) => {
        const h = (extractedHeaders[key] || "").toLowerCase();
        return h.includes("level") && h.includes("compact");
      })
      .map((key) => parseInt(key.replace("col", ""), 10))
      .sort((a, b) => a - b)
      .slice(0, 6);

    for (let i = 0; i < actualSectionStarts.length; i++) {
      const actual = actualSectionStarts[i];
      const expected = expectedSectionStarts[i];
      if (actual < expected) {
        const slotsToInsert = expected - actual;
        for (let s = 0; s < slotsToInsert; s++) {
          for (let colNum = 77; colNum > actual; colNum--) {
            const destKey = `col${colNum}`;
            const srcKey = `col${colNum - 1}`;
            if (columnIndexMap[srcKey] !== undefined) {
              columnIndexMap[destKey] = columnIndexMap[srcKey];
              extractedHeaders[destKey] = extractedHeaders[srcKey];
            } else {
              delete columnIndexMap[destKey];
              delete extractedHeaders[destKey];
            }
          }
          delete columnIndexMap[`col${actual}`];
          delete extractedHeaders[`col${actual}`];
          for (let j = i; j < actualSectionStarts.length; j++) {
            actualSectionStarts[j]++;
          }
        }
      }
    }

    // Extract dimension values from imported headers (same logic as PlotDrives component)
    const extractedDimensionValues: Record<string, string> = {};
    // Default column titles with dimension patterns (from PlotDrives excelColumns)
    const defaultColumnTitles: Record<string, string> = {
      col3: "xxxmm Sub base",
      col4: "xxmm DB  Base",
      col5: "xxmm DB Binder",
      col9: "xxxmm Additional / Capping",
      col10: "xxxmm Sub base",
      col11: "xxmm DB Base",
      col12: "xxmm DB Binder",
      col16: "xxmm DB binder Running course",
      col17: "xxmm 10-63mm clean aggregate sub base",
      col18: "xxmm 5-20mm clean aggregate sub base",
      col22: "xxxmm Sub base",
      col23: "xxmm DB  Base",
      col24: "xxmm DB Binder",
      col28: "xxxmm Sub base",
      col29: "xxmm DB Base",
      col30: "xxmm DB Binder",
      col35: "xxmm DB binder Running course",
      col36: "xxmm 10-63mm clean aggregate sub base",
      col37: "xxmm 5-20mm clean aggregate sub base",
      col43: "xxxx Kerb",
      col44: "xxxx Kerb",
      col45: "xxxx Kerb",
      // Surface course dynamic columns (new layout)
      col51: "xxmm HRA Surface course", // Private Macadam
      col52: "xxmm SMA Surface course", // Private Macadam
      col64: "xxmm HRA Surface course", // Shared Macadam
      col65: "xxmm SMA Surface course", // Shared Macadam
      col59: "xxmm thick 2-6mm laying Aggregate aggregate", // Private Permeable
      col71: "xxmm thick 2-6mm laying Aggregate aggregate", // Shared Permeable
    };

    // Configure Headers dropdown option sets — mirrors the identical option lists
    // PlotDrives.tsx's own Configure Headers modal builds (dbBaseOptions/
    // dbBinderOptions/dbSurfaceVariants/hraSurfaceVariants/smaSurfaceVariants/
    // block*/permeable*PaviorsOptions), duplicated here (same convention as the
    // binderRunningCourseOptions/sandLayingCourseOptions below) so an imported
    // header can be validated against the exact same picklist the live modal
    // offers, rather than accepted verbatim regardless of whether it matches
    // anything selectable.
    const dbBaseOptions = Object.values(EVALUATE_CODES).filter((l) => ["ALP041", "ALP042"].includes(l.code));
    const dbBinderOptions = Object.values(EVALUATE_CODES).filter((l) => ["ALP043", "ALP044", "ALP045", "ALP046", "ALP047"].includes(l.code));
    const dbSurfaceVariants: { code: string; label: string }[] = [
      { code: "ALP048", label: "20mm thick AC 6mm dense surf" },
      { code: "ALP049", label: "25mm thick AC 6mm dense surf" },
      { code: "ALP049", label: "25mm thick AC 10mm dense surf" },
      { code: "ALP051", label: "30mm thick AC 6mm dense surf" },
      { code: "ALP051", label: "30mm thick AC 10mm dense surf" },
    ];
    const hraSurfaceVariants: { code: string; label: string }[] = [
      { code: "ALP053", label: "25mm thick HRA 6mm" },
      { code: "ALP053", label: "25mm thick HRA 10mm" },
      { code: "ALP053", label: "30mm thick HRA 6mm" },
      { code: "ALP053", label: "30mm thick HRA 10mm" },
    ];
    const smaSurfaceVariants: { code: string; label: string }[] = [
      { code: "ALP052", label: "25mm thick SMA 6mm" },
      { code: "ALP052", label: "25mm thick SMA 10mm" },
      { code: "ALP052", label: "30mm thick SMA 6mm" },
      { code: "ALP052", label: "30mm thick SMA 10mm" },
    ];
    // Block/Permeable Paviors (60mm/80mm) are matched by brand keyword further
    // below, not by fuzzy label match — their option codes (GWALPD001-010)
    // are hardcoded directly into that keyword→code mapping instead of being
    // filtered from EVALUATE_CODES here, since a brand keyword ("charcon",
    // "keyblock", ...) rarely appears verbatim in the option's own label text.

    // Case-insensitive exact match first, then substring match either direction
    // (same fallback order the specialExtractionColumns dropdown match below
    // already uses) — returns the option whose *canonical* label/value should
    // be stored, not the raw imported text, so a near-miss import ("25mm HRA
    // 10mm thick" vs the option's "25mm thick HRA 10mm") still resolves to
    // the exact string the live dropdown would store.
    function fuzzyMatchLabel<T extends { label: string }>(importedHeader: string, options: T[]): T | undefined {
      const headerLower = importedHeader.toLowerCase();
      return (
        options.find((opt) => headerLower === opt.label.toLowerCase()) ||
        options.find((opt) => headerLower.includes(opt.label.toLowerCase()) || opt.label.toLowerCase().includes(headerLower))
      );
    }

    // Columns for Sub base, DB Base, DB Binder, Laying Course, Surface Course - use full header as-is (no dimension extraction)
    const useHeaderAsIsColumns = [
      "col3",
      "col10",
      "col22",
      "col28", // Sub base columns
      "col4",
      "col11",
      "col23",
      "col29", // DB Base columns
      "col5",
      "col12",
      "col24",
      "col30", // DB Binder columns
      // Surface course fixed columns (new layout - col48-col77)
      "col51",
      "col52", // Private Macadam - HRA, SMA (xxmm - stored as-is for full label)
      "col64",
      "col65", // Shared Macadam - HRA, SMA
      "col59",
      "col71", // 2-6mm laying Aggregate (Private/Shared Permeable)
    ];

    // DB binder Running course dropdown options for matching
    const binderRunningCourseOptions = [
      "50mm thick AC 20mm dense bin",
      "60mm thick AC 20mm dense bin",
      "75mm thick AC 20mm dense bin",
      "80mm thick AC 20mm dense bin",
      "100mm thick AC 20mm dense bin",
    ];

    // Sand Laying Course dropdown options for matching
    const sandLayingCourseOptions = [
      "30mm Sand Laying Course",
      "50mm Sand Laying Course",
    ];

    // Columns that need special extraction (leading dimension before fixed text)
    const specialExtractionColumns: Record<string, { fixedText: string; type: "dimension" | "dropdown"; options?: string[] }> = {
      col16: { fixedText: "DB binder Running course", type: "dropdown", options: binderRunningCourseOptions },
      col35: { fixedText: "DB binder Running course", type: "dropdown", options: binderRunningCourseOptions },
      col55: { fixedText: "sand Laying Course", type: "dropdown", options: sandLayingCourseOptions },
      col67: { fixedText: "sand Laying Course", type: "dropdown", options: sandLayingCourseOptions },
      col17: { fixedText: "63-10mm Sub Base - Varied Depths", type: "dimension" },
      col36: { fixedText: "63-10mm Sub Base - Varied Depths", type: "dimension" },
      col18: { fixedText: "5-20mm clean aggregate sub base", type: "dimension" },
      col37: { fixedText: "5-20mm clean aggregate sub base", type: "dimension" },
    };

    // Only columns that actually appear in PlotDrives.tsx's Configure Headers
    // modal (`dimensionFields`/`filteredFields` there) may ever have their
    // column title/dimension value touched by import — every other column
    // ("Level and compact formation", "Geotextile Layer", "Sealing Grit",
    // the various Edgings/Kerb-type/Puncture Macadam/Sweep columns, etc.) is
    // a fixed, non-configurable field with no Configure Headers entry at all,
    // and must always keep showing its own hardcoded default title regardless
    // of whatever text happens to sit in that Excel column's header cell.
    // Mirrors PlotDrives.tsx's own eligibility test exactly: the column's
    // DEFAULT title contains a dimension placeholder (xx/xxx/xxxx, optionally
    // "mm"), or it's one of the 8 paviors columns, or one of the 3 kerb
    // columns (the latter two have no such placeholder in their own title).
    const CONFIGURE_HEADERS_ELIGIBLE_COLUMNS = new Set([
      "col3", "col4", "col5", "col9", "col10", "col11", "col16", "col17", "col18",
      "col22", "col23", "col24", "col28", "col29", "col30", "col35", "col36", "col37",
      "col43", "col44", "col45",
      "col50", "col51", "col52", "col55", "col59",
      "col63", "col64", "col65", "col67", "col71",
      "col56", "col57", "col60", "col61", "col68", "col69", "col72", "col73",
    ]);
    Object.keys(extractedHeaders).forEach((colKey) => {
      if (!CONFIGURE_HEADERS_ELIGIBLE_COLUMNS.has(colKey)) {
        delete extractedHeaders[colKey];
      }
    });

    Object.keys(extractedHeaders).forEach((colKey) => {
      const importedHeader = String(extractedHeaders[colKey] || "").trim();
      const defaultTitle = defaultColumnTitles[colKey];

      // DB Base / DB Binder (col4,11,23,29 / col5,12,24,30) and HRA/SMA Surface
      // (col51,64 / col52,65) are genuine Configure Headers dropdowns — only
      // apply the import if the header text actually matches one of that
      // field's options (same picklists PlotDrives.tsx's Configure Headers
      // modal offers); otherwise leave whatever was already configured alone.
      // `extractedHeaders[colKey]` was already populated verbatim above this
      // loop — delete it on a non-match so the raw, unrecognised text doesn't
      // leak through as the column title either.
      const dbBaseKeys = ["col4", "col11", "col23", "col29"];
      const dbBinderKeys = ["col5", "col12", "col24", "col30"];
      const hraSurfaceKeys = ["col51", "col64"];
      const smaSurfaceKeys = ["col52", "col65"];
      if (dbBaseKeys.includes(colKey) || dbBinderKeys.includes(colKey)) {
        const match = fuzzyMatchLabel(importedHeader, dbBaseKeys.includes(colKey) ? dbBaseOptions : dbBinderOptions);
        if (match) {
          extractedHeaders[colKey] = match.label;
          extractedDimensionValues[colKey] = match.label;
        } else {
          delete extractedHeaders[colKey];
        }
        return;
      }
      if (hraSurfaceKeys.includes(colKey) || smaSurfaceKeys.includes(colKey)) {
        const match = fuzzyMatchLabel(importedHeader, hraSurfaceKeys.includes(colKey) ? hraSurfaceVariants : smaSurfaceVariants);
        if (match) {
          extractedHeaders[colKey] = match.label;
          extractedDimensionValues[colKey] = match.code;
        } else {
          delete extractedHeaders[colKey];
        }
        return;
      }

      // Remaining useHeaderAsIsColumns entries (Sub base mm thickness, 2-6mm
      // laying Aggregate mm thickness) are NumberInput-driven, not dropdowns —
      // the header text itself is never picked from a fixed list, it's built
      // from whatever mm value is entered. Still, only a header that actually
      // *starts with a number* (e.g. "400mm thick MOT Type 1") is a real
      // configuration — a non-numeric placeholder/garbage string (e.g.
      // "testmm thick MOT Type 1") is rejected rather than stored, since
      // NumberInput's parseFloat would just read it as 0 anyway, silently
      // discarding whatever thickness had actually been configured before.
      if (useHeaderAsIsColumns.includes(colKey)) {
        if (importedHeader && /^\d+(\.\d+)?/.test(importedHeader)) {
          extractedDimensionValues[colKey] = importedHeader;
        } else {
          delete extractedHeaders[colKey];
        }
        return;
      }

      // Special extraction for columns with fixed text after the dynamic dimension
      const specialConfig = specialExtractionColumns[colKey];
      if (specialConfig) {
        if (specialConfig.type === "dropdown" && specialConfig.options) {
          // Match against dropdown options (e.g., binder running course)
          // Try exact match first, then includes match (both directions)
          const headerLower = importedHeader.toLowerCase();
          const matchedOption = specialConfig.options.find(
            (opt) => headerLower === opt.toLowerCase()
          ) || specialConfig.options.find(
            (opt) => headerLower.includes(opt.toLowerCase()) || opt.toLowerCase().includes(headerLower)
          );
          if (matchedOption) {
            extractedHeaders[colKey] = matchedOption;
            extractedDimensionValues[colKey] = matchedOption;
          } else {
            delete extractedHeaders[colKey];
          }
        } else {
          // Extract leading dimension before the fixed text
          // e.g., "200mm 5-20mm clean aggregate sub base" → extract "200"
          // e.g., "63-10mm Sub Base - Varied Depths" → no leading dimension, skip
          const fixedTextIndex = importedHeader.toLowerCase().indexOf(specialConfig.fixedText.toLowerCase());
          const leadingText = fixedTextIndex > 0 ? importedHeader.substring(0, fixedTextIndex).trim() : "";
          const dimMatch = leadingText.match(/^(\d+(?:\.\d+)?)/);
          if (dimMatch) {
            extractedDimensionValues[colKey] = dimMatch[1];
          } else {
            delete extractedHeaders[colKey];
          }
        }
        return;
      }

      // DB Surface course (col50, col63) — genuine dropdown, matched directly
      // against its own variant labels (same as DB Base/Binder/HRA/SMA above)
      // rather than a broad EVALUATE_CODES label scan + dimension guess, since
      // several variants share one code and only the label disambiguates which
      // option the live Configure Headers select would actually show selected.
      if (["col50", "col63"].includes(colKey)) {
        const match = fuzzyMatchLabel(importedHeader, dbSurfaceVariants);
        if (match) {
          extractedHeaders[colKey] = match.label;
          extractedDimensionValues[colKey] = match.code;
        } else {
          delete extractedHeaders[colKey];
        }
        return;
      }

      // Block/Permeable Paviors columns - match to evaluate codes by brand keyword
      const paviorsColumns = [
        "col56", "col57", // Private Block Paved 60mm/80mm
        "col68", "col69", // Shared Block Paved 60mm/80mm
        "col60", "col61", // Private Permeable Paving 60mm/80mm
        "col72", "col73", // Shared Permeable Paving 60mm/80mm
      ];
      if (paviorsColumns.includes(colKey)) {
        const header = importedHeader.toLowerCase();
        let matchedCode: string | null = null;

        // 60mm Block Paviors - match by brand keywords only
        if (["col56", "col68"].includes(colKey)) {
          if (header.includes("charcon") || header.includes("europa")) matchedCode = "GWALPD002";
          else if (header.includes("keyblock")) matchedCode = "GWALPD010";
          else if (header.includes("tegula")) matchedCode = "GWALPD006";
        }
        // 80mm Block Paviors - match by brand keywords only
        if (["col57", "col69"].includes(colKey)) {
          if (header.includes("charcon") || header.includes("europa")) matchedCode = "GWALPD001";
          else if (header.includes("keyblock")) matchedCode = "GWALPD009";
          else if (header.includes("tegula")) matchedCode = "GWALPD005";
        }
        // 60mm Permeable Paviors - match by brand keywords only
        if (["col60", "col72"].includes(colKey)) {
          if (header.includes("charcon") || header.includes("infilta")) matchedCode = "GWALPD004";
          else if (header.includes("marshalls") || header.includes("priora")) matchedCode = "GWALPD008";
        }
        // 80mm Permeable Paviors - match by brand keywords only
        if (["col61", "col73"].includes(colKey)) {
          if (header.includes("charcon") || header.includes("infilta")) matchedCode = "GWALPD003";
          else if (header.includes("marshalls") || header.includes("priora")) matchedCode = "GWALPD007";
        }

        // Only apply when a brand keyword actually matched — no fallback to
        // the raw imported text, so an unrecognised brand leaves whatever was
        // already configured untouched instead of showing unselectable text.
        if (matchedCode) {
          extractedDimensionValues[colKey] = matchedCode;
          extractedHeaders[colKey] = EVALUATE_CODES[matchedCode as EvaluateCode]?.label || importedHeader;
        } else {
          delete extractedHeaders[colKey];
        }
        return;
      }

      // Kerb columns (col43-col45): extract prefix before " Kerb" — can be text ("PCC") or number ("1000")
      if (["col43", "col44", "col45"].includes(colKey)) {
        const kerbPrefixMatch = importedHeader.match(/^(.+?)\s+[Kk]erb/i);
        if (kerbPrefixMatch) {
          extractedDimensionValues[colKey] = kerbPrefixMatch[1].trim();
        } else if (importedHeader && !importedHeader.toLowerCase().includes("xxxx")) {
          // Header has no " Kerb" suffix but is not the placeholder — use as-is
          extractedDimensionValues[colKey] = importedHeader;
        } else {
          // Blank/placeholder ("xxxx Kerb") — no real configuration to import
          delete extractedHeaders[colKey];
        }
        return;
      }

      if (defaultTitle && /\b(x{2,}(?:mm)?)\b/gi.test(defaultTitle)) {
        const dimensionMatch = importedHeader.match(/^(\d+(?:\.\d+)?(?:x\d+(?:\.\d+)?)?)/i);
        if (dimensionMatch) {
          extractedDimensionValues[colKey] = dimensionMatch[1];
        } else {
          delete extractedHeaders[colKey];
        }
      }
    });
    // Data starts from Row 4 (index 4) - right after Row 3 headers
    const allDataRows = jsonData.slice(headerRowIndex + 1);
    // Filter out rows with totals, sums, or empty values
    const dataRows = allDataRows.filter((row) => {
      if (!row || row.length === 0) return false;

      const plotNoValue = String(row[0] || "")
        .trim()
        .toLowerCase();
      const houseTypeValue = String(row[1] || "")
        .trim()
        .toLowerCase();

      // Skip rows that are totals, sums, or completely empty
      if (
        plotNoValue.includes("total") ||
        plotNoValue.includes("sum") ||
        houseTypeValue.includes("total") ||
        houseTypeValue.includes("sum") ||
        (plotNoValue === "" && houseTypeValue === "")
      ) {
        return false;
      }

      return true;
    });
    // Map all rows with 73 columns
    const allImportedRows = dataRows
      .filter((row) => row && row.length > 0)
      .map((row, index) => {
        const rowData: any = {
          key: `imported_${Date.now()}_${index}`,
          plotNo: String(row[plotNoIdx] || ""),
          housetype: String(row[housetypeIdx] || ""),
        };

        // Map all 77 columns using the columnIndexMap
        for (let colNum = 1; colNum <= 77; colNum++) {
          const colKey = `col${colNum}`;
          const excelColIndex = columnIndexMap[colKey];
          if (excelColIndex !== undefined) {
            rowData[colKey] =
              Math.round((Number(row[excelColIndex]) || 0) * 1000) / 1000;
          } else {
            rowData[colKey] = 0;
          }
        }

        return rowData;
      })
      .filter((row) => {
        const plotNoStr = String(row.plotNo || "").trim();
        const hasPlotNo =
          plotNoStr !== "" &&
          plotNoStr !== "0" &&
          plotNoStr !== "null" &&
          plotNoStr !== "undefined";
        const housetypeStr = String(row.housetype || "").trim();
        const hasValidHouseType =
          housetypeStr !== "" &&
          !housetypeStr.toLowerCase().includes("total") &&
          !housetypeStr.toLowerCase().includes("sum") &&
          isNaN(Number(housetypeStr)) && // Not purely numeric
          housetypeStr.match(/[a-zA-Z]/); // Contains letters
        return hasPlotNo && hasValidHouseType;
      });
    return {
      data: allImportedRows,
      columnHeaders: extractedHeaders,
      dimensionValues: extractedDimensionValues,
    };
  };

  // Helper function to process Retaining Walls data
  const processRetainingWallsData = (jsonData: any[][]) => {
    // Find header row
    let headerRowIndex = -1;
    for (let i = 0; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (
        row.some(
          (cell) =>
            String(cell).includes("Plot") && String(cell).includes("No"),
        )
      ) {
        headerRowIndex = i;
        break;
      }
    }

    if (headerRowIndex === -1) {
      console.error("Could not find header row");
      return [];
    }

    const headers = jsonData[headerRowIndex];
    const allDataRows = jsonData.slice(headerRowIndex + 1);
    const dataRows = allDataRows.slice(1); // Skip totals row

    const plotNoIdx = 0;
    const housetypeIdx = 1;

    const allImportedRows = dataRows
      .filter((row) => row && row.length > 0)
      .map((row, index) => ({
        key: `imported_${Date.now()}_${index}`,
        plotNo: String(row[plotNoIdx] || ""),
        housetype: String(row[housetypeIdx] || ""),
        // Screen Walls (7 fields) - cols 4-10
        screenWalls: Math.round(Number(row[4] || 0) * 1000) / 1000,
        screenWallsEO: Math.round(Number(row[5] || 0) * 1000) / 1000,
        screenWallsSof: Math.round(Number(row[6] || 0) * 1000) / 1000,
        screenWallsLog: Math.round(Number(row[7] || 0) * 1000) / 1000,
        screenWallsGravelBoard: Math.round(Number(row[8] || 0) * 1000) / 1000,
        screenWallsAdditionalExcavation: Math.round(Number(row[9] || 0) * 1000) / 1000,
        screenWallsAdditionalConcrete: Math.round(Number(row[10] || 0) * 1000) / 1000,
        // Garden Retaining Walls (10 fields) - cols 11-20
        grw450: Math.round(Number(row[11] || 0) * 1000) / 1000,
        grw600: Math.round(Number(row[12] || 0) * 1000) / 1000,
        grw750: Math.round(Number(row[13] || 0) * 1000) / 1000,
        grw900: Math.round(Number(row[14] || 0) * 1000) / 1000,
        grw1050: Math.round(Number(row[15] || 0) * 1000) / 1000,
        grw1200: Math.round(Number(row[16] || 0) * 1000) / 1000,
        grw1500: Math.round(Number(row[17] || 0) * 1000) / 1000,
        grw1800: Math.round(Number(row[18] || 0) * 1000) / 1000,
        grw2000: Math.round(Number(row[19] || 0) * 1000) / 1000,
        // Drive Retaining Walls (9 fields) - cols 24-32 (3 empty cols 21-23 skipped)
        drw450: Math.round(Number(row[24] || 0) * 1000) / 1000,
        drw600: Math.round(Number(row[25] || 0) * 1000) / 1000,
        drw750: Math.round(Number(row[26] || 0) * 1000) / 1000,
        drw900: Math.round(Number(row[27] || 0) * 1000) / 1000,
        drw1050: Math.round(Number(row[28] || 0) * 1000) / 1000,
        drw1200: Math.round(Number(row[29] || 0) * 1000) / 1000,
        drw1500: Math.round(Number(row[30] || 0) * 1000) / 1000,
        drw1800: Math.round(Number(row[31] || 0) * 1000) / 1000,
        drw2000: Math.round(Number(row[32] || 0) * 1000) / 1000,
        // Concrete Core Walls (9 fields) - cols 33-41
        ccrw1500: Math.round(Number(row[33] || 0) * 1000) / 1000,
        ccrw1800: Math.round(Number(row[34] || 0) * 1000) / 1000,
        ccrw2000: Math.round(Number(row[35] || 0) * 1000) / 1000,
        ccrw2250: Math.round(Number(row[36] || 0) * 1000) / 1000,
        ccrw2500: Math.round(Number(row[37] || 0) * 1000) / 1000,
        ccrw2750: Math.round(Number(row[38] || 0) * 1000) / 1000,
        ccrw3000: Math.round(Number(row[39] || 0) * 1000) / 1000,
        ccrw3250: Math.round(Number(row[40] || 0) * 1000) / 1000,
        ccrw3500: Math.round(Number(row[41] || 0) * 1000) / 1000,
      }))
      .filter((row) => {
        const hasPlotNo =
          row.plotNo && row.plotNo.trim() !== "" && row.plotNo.trim() !== "0";
        const housetypeStr = String(row.housetype || "").trim();
        const hasValidHouseType =
          housetypeStr !== "" &&
          !housetypeStr.toLowerCase().includes("total") &&
          !housetypeStr.toLowerCase().includes("sum") &&
          isNaN(Number(housetypeStr)) && // Not purely numeric
          housetypeStr.match(/[a-zA-Z]/); // Contains letters
        return hasPlotNo && hasValidHouseType;
      });
    return allImportedRows;
  };

  // Helper function to extract Geowall & Tobermore data from "RET & SCREEN TAKE OFF" sheet
  // Layout (0-indexed): after retaining walls data (ends at col 41 CCRW3500):
  //   col 42: GPCW600, col 43: GPCW750, col 44: GPCW900, col 45: GPCW1050
  //   col 46: TPCW1, col 47: TPCW2, ..., col 53: TPCW8
  const processGeoToberWallFromRetScreenData = (jsonData: any[][]) => {
    // Find header row
    let headerRowIndex = -1;
    for (let i = 0; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (row.some((cell) => String(cell).includes("Plot") && String(cell).includes("No"))) {
        headerRowIndex = i;
        break;
      }
    }

    if (headerRowIndex === -1) {
      return { data: [], columnHeaders: {}, dimensionValues: {} };
    }

    const allDataRows = jsonData.slice(headerRowIndex + 1);
    const dataRows = allDataRows.slice(1); // Skip totals row

    // Fixed column positions: GPCW starts at col 42, TPCW at col 46
    const GPCW600_COL = 42;
    const GPCW750_COL = 43;
    const GPCW900_COL = 44;
    const GPCW1050_COL = 45;
    const TPCW_START_COL = 46; // tpcw1=46, tpcw2=47, ..., tpcw8=53

    // Column names (GPCW600, TPCW 1600mm, etc.) live 3 rows below the "Plot
    // No" header row (header+1: dimension numbers, header+2: units,
    // header+3: these codes/labels) - previously hardcoded as the sheet's
    // absolute row index 3, which is actually the units row ("m", "m", ...)
    // one row too early. "m" has no digits, so every TPCW dimension match
    // failed, `validTpcwKeys` stayed empty, and `getTpcwNum` returned 0 for
    // every plot regardless of the real value in the sheet - Tobermore
    // (TPCW) column headers and quantities both silently came through empty
    // on import while Geowall (GPCW, not label-driven) worked fine. Deriving
    // this relative to `headerRowIndex` (rather than a second hardcoded
    // absolute index) keeps it correct if the header row ever shifts.
    // Same approach as Plot Drives: read actual header text, extract numeric
    // dimension. Only store header + dimension if a real numeric dimension is
    // present; placeholder headers like "TPCW xxxxmm" are skipped so the
    // dynamic TPCW slots stay empty until the user configures them.
    const columnNameRow = jsonData[headerRowIndex + 3] || [];
    const extractedColumnHeaders: Record<string, string> = {};
    const extractedDimensionValues: Record<string, string> = {};
    const validTpcwKeys = new Set<string>();
    for (let i = 0; i < 8; i++) {
      const colIdx = TPCW_START_COL + i;
      const key = `tpcw${i + 1}`;
      const headerStr = String(columnNameRow[colIdx] || "").trim();
      if (!headerStr) continue;
      // Extract the numeric dimension value (e.g. "450") — same as Plot Drives pattern
      const dimMatch = headerStr.match(/\b(\d+)(?:\s*mm)?\b/i);
      if (!dimMatch) continue; // Skip placeholder headers (e.g. "TPCW xxxxmm")
      extractedColumnHeaders[key] = headerStr;
      extractedDimensionValues[key] = dimMatch[1];
      validTpcwKeys.add(key);
    }

    const getNum = (row: any[], idx: number): number =>
      Math.round(Number(row[idx] || 0) * 1000) / 1000;
    // Read a TPCW column value only when the slot is configured (has a real
    // numeric dimension on the takeoff header row). Placeholder slots stay 0.
    const getTpcwNum = (row: any[], slotIdx: number): number => {
      const key = `tpcw${slotIdx + 1}`;
      return validTpcwKeys.has(key) ? getNum(row, TPCW_START_COL + slotIdx) : 0;
    };

    const allImportedRows = dataRows
      .filter((row) => row && row.length > 0)
      .map((row, index) => ({
        key: `imported_${Date.now()}_${index}`,
        plotNo: String(row[0] || ""),
        housetype: String(row[1] || ""),
        geoWallGPCW600: getNum(row, GPCW600_COL),
        gpcw750: getNum(row, GPCW750_COL),
        geoWallRW900: getNum(row, GPCW900_COL),
        gpcw1050: getNum(row, GPCW1050_COL),
        tpcw1: getTpcwNum(row, 0),
        tpcw2: getTpcwNum(row, 1),
        tpcw3: getTpcwNum(row, 2),
        tpcw4: getTpcwNum(row, 3),
        tpcw5: getTpcwNum(row, 4),
        tpcw6: getTpcwNum(row, 5),
        tpcw7: getTpcwNum(row, 6),
        tpcw8: getTpcwNum(row, 7),
      }))
      .filter((row) => {
        const hasPlotNo =
          row.plotNo && row.plotNo.trim() !== "" && row.plotNo.trim() !== "0";
        const housetypeStr = String(row.housetype || "").trim();
        const hasValidHouseType =
          housetypeStr !== "" &&
          !housetypeStr.toLowerCase().includes("total") &&
          !housetypeStr.toLowerCase().includes("sum") &&
          isNaN(Number(housetypeStr)) && // Not purely numeric
          housetypeStr.match(/[a-zA-Z]/); // Contains letters
        return hasPlotNo && hasValidHouseType;
      });

    return {
      data: allImportedRows,
      columnHeaders: extractedColumnHeaders,
      dimensionValues: extractedDimensionValues,
    };
  };

  type BuildUpDimItem = { description: string; length: number; width: number; height: number; net: number; unit: string };

  // Generic block parser — same structure as processWallsBuildUpData but accepts a custom specId map
  const parseWallBuildUpBlocks = (
    jsonData: any[][],
    specIdMap: (cell: string) => string | null,
  ): Record<string, BuildUpDimItem[]> => {
    const result: Record<string, BuildUpDimItem[]> = {};
    let rowIdx = 0;
    while (rowIdx < jsonData.length) {
      const row = jsonData[rowIdx] || [];
      const blockSpecIds = WB_BLOCK_OFFSETS.map((offset) => specIdMap(String(row[offset] ?? "").trim()));
      if (!blockSpecIds.some((id) => id !== null)) { rowIdx++; continue; }
      // Only process specs not yet collected — skips duplicate header rows (e.g. totals sections)
      const activeSpecIds = blockSpecIds.map((id) => (id && !(result[id]?.length > 0) ? id : null));
      if (!activeSpecIds.some((id) => id !== null)) { rowIdx++; continue; }
      activeSpecIds.forEach((specId) => { if (specId) result[specId] = []; });
      rowIdx++;
      while (rowIdx < jsonData.length) {
        const itemRow = jsonData[rowIdx] || [];
        if (isWallsBuildUpSummaryRow(itemRow)) {
          while (rowIdx < jsonData.length && isWallsBuildUpSummaryRow(jsonData[rowIdx] || [])) rowIdx++;
          break;
        }
        // Require >=2 block offsets to match before treating as next block header.
        // A single match is likely a sub-label row ("RW ne 900mm" etc.) inside the
        // current block's items — that must NOT break item collection early.
        if (WB_BLOCK_OFFSETS.map((offset) => specIdMap(String(itemRow[offset] ?? "").trim())).filter((id) => id !== null).length >= 2) break;
        WB_BLOCK_OFFSETS.forEach((offset, blockIdx) => {
          const specId = activeSpecIds[blockIdx];
          if (!specId) return;
          const description = String(itemRow[offset] ?? "").trim();
          if (!description) return;
          // Skip section label rows: "RW ne xxxmm", "Precast walls ...",
          // "TPCW xxxxmm" placeholders (Tobermore master sheet labels that were
          // bleeding into the 450 build-up as fake items), etc.
          if (/^(rw\s*ne\b|precast\s+walls|tpcw\b)/i.test(description) || specIdMap(description) !== null) return;
          const length = Number(itemRow[offset + 1]) || 0;
          const width  = Number(itemRow[offset + 2]) || 0;
          const height = Number(itemRow[offset + 3]) || 0;
          const net    = Number(itemRow[offset + 4]) || 0;
          const unit   = String(itemRow[offset + 5] ?? "").trim();
          result[specId].push({ description, length, width, height, net, unit });
        });
        rowIdx++;
      }
    }
    return result;
  };

  // Geowall spec ID resolver for "GEOWALL MASTER" sheet
  // Headers use "RW ne 600mm", "RW ne 750mm", "RW ne 900mm", "RW ne 1050mm"
  const resolveGeoWallSpecId = (cell: string): string | null => {
    const norm = cell.toLowerCase().replace(/\s+/g, " ").trim();
    if (norm.includes("600")) return 'geowall_gpcw600';
    if (norm.includes("750")) return 'geowall_gpcw750';
    if (norm.includes("900")) return 'geowall_rw_900';
    if (norm.includes("1050")) return 'geowall_gpcw1050';
    return null;
  };

  const processGeoWallMasterData = (jsonData: any[][]): Record<string, BuildUpDimItem[]> =>
    parseWallBuildUpBlocks(jsonData, resolveGeoWallSpecId);

  // Tobermore spec ID map built from configured column headers (e.g. "TPCW 450mm" → tobermore_tpcw1)
  const processTobMoreMasterData = (
    jsonData: any[][],
    columnHeaders: Record<string, string>,
  ): Record<string, BuildUpDimItem[]> => {
    // Build reverse map: normalised header text → spec ID
    const headerToSpecId: Record<string, string> = {};
    Object.entries(columnHeaders).forEach(([key, headerText]) => {
      if (headerText) headerToSpecId[headerText.trim().toUpperCase()] = `tobermore_${key}`;
    });
    return parseWallBuildUpBlocks(jsonData, (cell) => headerToSpecId[cell.toUpperCase()] ?? null);
  };

  // Maps Excel header names (upper-cased) to spec IDs used in walls-section-build-up mock
  const WALL_BUILD_UP_NAME_TO_SPEC_ID: Record<string, string> = {
    GRW450: "grw450", GRW600: "grw600", GRW750: "grw750",
    GRW900: "grw900", GRW1050: "grw1050", GRW1200: "grw1200",
    GRW1500: "grw1500", GRW1800: "grw1800", GRW2000: "grw2000",
    DRW450: "drw450", DRW600: "drw600", DRW750: "drw750",
    DRW900: "drw900", DRW1050: "drw1050", DRW1200: "drw1200",
    DRW1500: "drw1500", DRW1800: "drw1800", DRW2000: "drw2000",
    DRW2250: "drw2250", DRW2500: "drw2500", DRW2750: "drw2750",
    DRW3000: "drw3000",
    CCRW1500: "ccrw1500", CCRW1800: "ccrw1800", CCRW2000: "ccrw2000",
    CCRW2250: "ccrw2250", CCRW2500: "ccrw2500", CCRW2750: "ccrw2750",
    CCRW3000: "ccrw3000", CCRW3250: "ccrw3250", CCRW3500: "ccrw3500",
  };

  // Each block is 9 cols wide (Desc|L|W|H|Net|Unit|Rate|£Amount|0.00); up to 4 blocks per section
  const WB_BLOCK_OFFSETS = [0, 9, 18, 27];
  const WB_SUMMARY_LABELS = new Set(["Net", "OHP", "Rate"]);

  // Summary row: empty description at col 0, any cell equals a summary label
  const isWallsBuildUpSummaryRow = (row: any[]) => {
    if (String(row[0] ?? "").trim()) return false;
    return row.some((cell) => WB_SUMMARY_LABELS.has(String(cell ?? "").trim()));
  };

  const getWallBlockSpecIds = (row: any[]) =>
    WB_BLOCK_OFFSETS.map((offset) => {
      const cell = String(row[offset] ?? "").trim().toUpperCase();
      return WALL_BUILD_UP_NAME_TO_SPEC_ID[cell] ?? null;
    });

  // Parse "Brick&con wall section build up" sheet: 4 wall-spec blocks side-by-side, each 9 cols wide
  const processWallsBuildUpData = (
    jsonData: any[][]
  ): Record<string, Array<{ description: string; length: number; width: number; height: number; net: number; unit: string }>> => {
    const result: Record<string, Array<{ description: string; length: number; width: number; height: number; net: number; unit: string }>> = {};

    let rowIdx = 0;
    while (rowIdx < jsonData.length) {
      const row = jsonData[rowIdx] || [];
      const blockSpecIds = getWallBlockSpecIds(row);

      if (!blockSpecIds.some((id) => id !== null)) {
        rowIdx++;
        continue;
      }

      blockSpecIds.forEach((specId) => {
        if (specId && !result[specId]) result[specId] = [];
      });

      rowIdx++;

      // Collect item rows until summary rows or next section header
      while (rowIdx < jsonData.length) {
        const itemRow = jsonData[rowIdx] || [];

        if (isWallsBuildUpSummaryRow(itemRow)) {
          while (rowIdx < jsonData.length && isWallsBuildUpSummaryRow(jsonData[rowIdx] || [])) {
            rowIdx++;
          }
          break;
        }

        if (getWallBlockSpecIds(itemRow).some((id) => id !== null)) break;

        WB_BLOCK_OFFSETS.forEach((offset, blockIdx) => {
          const specId = blockSpecIds[blockIdx];
          if (!specId) return;
          const description = String(itemRow[offset] ?? "").trim();
          if (!description) return;
          result[specId].push({
            description,
            length: Number(itemRow[offset + 1]) || 0,
            width:  Number(itemRow[offset + 2]) || 0,
            height: Number(itemRow[offset + 3]) || 0,
            net:    Number(itemRow[offset + 4]) || 0,
            unit:   String(itemRow[offset + 5] ?? "").trim(),
          });
        });

        rowIdx++;
      }
    }

    return result;
  };

  // Helper function to process Plot Service data
  const processPlotServiceData = (jsonData: any[][]) => {
    // Find the header row: the row containing the column headers
    let headerRowIndex = -1;

    // Pass 1: find a row with "plots" AND another service keyword
    for (let i = 0; i < Math.min(15, jsonData.length); i++) {
      const row = jsonData[i] || [];
      const cells = row.map((c: any) => String(c || "").toLowerCase().trim());
      const hasPlots = cells.some(
        (c) => c === "plots" || c.includes("excavate") || c.includes("service trench"),
      );
      const hasOther = cells.some(
        (c) =>
          c === "apartments" ||
          c.includes("barrier") ||
          c.includes("vac-ex") ||
          c.includes("vacex") ||
          c.includes("virgin media") ||
          c.includes("bollard") ||
          c.includes("charging"),
      );
      if (hasPlots && hasOther) {
        headerRowIndex = i;
        break;
      }
    }

    // Pass 2: fall back to any row with multiple service-related keywords
    if (headerRowIndex === -1) {
      for (let i = 0; i < Math.min(15, jsonData.length); i++) {
        const row = jsonData[i] || [];
        const rowText = row.map((c: any) => String(c || "").toLowerCase()).join(" ");
        const matchCount = [
          "barrier",
          "virgin",
          "bollard",
          "charging",
          "vac",
          "heat pump",
          "lighting",
        ].filter((kw) => rowText.includes(kw)).length;
        if (matchCount >= 2) {
          headerRowIndex = i;
          break;
        }
      }
    }

    if (headerRowIndex === -1) {
      console.error("processPlotServiceData: could not find header row");
      return [];
    }

    const headers = jsonData[headerRowIndex];

    const findColIdx = (keywords: string[]): number => {
      for (let i = 0; i < headers.length; i++) {
        const cell = String(headers[i] || "").toLowerCase().trim();
        if (keywords.every((kw) => cell.includes(kw.toLowerCase()))) return i;
      }
      return -1;
    };

    const findAllColIdx = (keywords: string[]): number[] => {
      const result: number[] = [];
      for (let i = 0; i < headers.length; i++) {
        const cell = String(headers[i] || "").toLowerCase().trim();
        if (keywords.every((kw) => cell.includes(kw.toLowerCase()))) result.push(i);
      }
      return result;
    };

    const plotsAll = findAllColIdx(["plots"]);
    const plotsIdx = plotsAll[0] ?? -1;
    const serviceConnectionPlotsIdx = plotsAll[1] ?? -1;

    const apartmentsAll = findAllColIdx(["apartments"]);
    const apartmentsIdx = apartmentsAll[0] ?? -1;
    const serviceConnectionApartmentsIdx = apartmentsAll[1] ?? -1;

    const eo25mmIdx = findColIdx(["barrier", "25"]);
    const eo32mmIdx = findColIdx(["barrier", "32"]);
    const virginMediaIdx = findColIdx(["virgin"]);
    const garageDuctIdx =
      findColIdx(["garage"]) !== -1 ? findColIdx(["garage"]) : findColIdx(["house", "garage"]);
    const privateLightIdx =
      findColIdx(["lighting"]) !== -1
        ? findColIdx(["lighting"])
        : findColIdx(["light", "duct"]);
    const pccEdgingsIdx =
      findColIdx(["pcc"]) !== -1 ? findColIdx(["pcc"]) : findColIdx(["gas", "box"]);
    const ductingChargingIdx = findColIdx(["ducting"]);
    const concBaseIdx =
      findColIdx(["conc", "base"]) !== -1
        ? findColIdx(["conc", "base"])
        : findColIdx(["concrete", "base"]);
    const bollardIdx = findColIdx(["bollard"]);
    const airSourceHeatPumpIdx =
      findColIdx(["heat", "pump"]) !== -1
        ? findColIdx(["heat", "pump"])
        : findColIdx(["air", "source"]);
    const vacExIdx =
      findColIdx(["vac-ex"]) !== -1 ? findColIdx(["vac-ex"]) : findColIdx(["vac"]);

    const getValue = (row: any[], idx: number): number =>
      idx !== -1 ? Math.round((Number(row[idx]) || 0) * 1000) / 1000 : 0;

    const dataRows = jsonData.slice(headerRowIndex + 1);

    return dataRows
      .filter((row) => row && row.length > 0)
      .map((row, index) => ({
        key: `imported_${Date.now()}_${index}`,
        plots: getValue(row, plotsIdx),
        apartments: getValue(row, apartmentsIdx),
        eo25mmBarrierPipe: getValue(row, eo25mmIdx),
        eo32mmBarrierPipe: getValue(row, eo32mmIdx),
        virginMedia: getValue(row, virginMediaIdx),
        garageDuct: getValue(row, garageDuctIdx),
        privateStLightDuct: getValue(row, privateLightIdx),
        pccEdgingsGasBoxes: getValue(row, pccEdgingsIdx),
        ductingToChargingPoint: getValue(row, ductingChargingIdx),
        concBaseChargingPoints: getValue(row, concBaseIdx),
        installBollardBase: getValue(row, bollardIdx),
        airSourceHeatPump: getValue(row, airSourceHeatPumpIdx),
        serviceConnectionPlots: getValue(row, serviceConnectionPlotsIdx),
        serviceConnectionApartments: getValue(row, serviceConnectionApartmentsIdx),
        vacEx: getValue(row, vacExIdx),
      }))
      .filter((row) =>
        Object.entries(row).some(([k, v]) => k !== "key" && typeof v === "number" && v > 0),
      )
      // Plot Services is a single aggregate row (no plot/house type columns),
      // so only the first row with real data is wanted - trailing template
      // rows in the sheet must not be imported too.
      .slice(0, 1);
  };

